import { BreakInfoStatusEnum } from '../types/break/break.types';
import { DiscountType } from '../types/discount/discount.types';
import { PaymentMethod, PaymentState, PaymentStatus } from '../types/payment/payment.types';
import { LogReason, LogSubtype, LogType } from '../types/restaurant/cashDrawerLogs.types';
import { OrderSource, OrderType, StatusType, TicketPaymentStatus } from '../types/ticket/ticket.types';
import { EmployeePayTypeEnum } from '../types/user-roles/user-roles.types';

export function getTotalSalesWithCardTipsQuery(current_tenant: string, partialPaymentStatusDisabled = false) {
  const paymentFilter = partialPaymentStatusDisabled
    ? `
    AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
    AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.PartialPaid}')
  `
    : `
    AND t."ticketPaymentStatus" IN (
      '${TicketPaymentStatus.FullPaid}',
      '${TicketPaymentStatus.PartialPaid}',
      '${TicketPaymentStatus.PartialRefund}',
      '${TicketPaymentStatus.FullRefund}'
    )
  `;

  const extraExclusions = partialPaymentStatusDisabled
    ? `('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.PartialPaid}')`
    : `('${TicketPaymentStatus.Unpaid}')`;

  const query = `
    with refund_cte AS (
      select
        sum(coalesce(r.amount, 0)) as amount,
        sum(coalesce(r."refundedAmount", 0)) as "refundedAmount",
        sum(CASE WHEN r."paymentMethod" = 'card' THEN coalesce(r."refundedTip", 0) ELSE 0 END) as "cardRefundedTip",
		    sum(CASE WHEN r."paymentMethod" = 'tender' THEN coalesce(r."refundedTip", 0) ELSE 0 END) as "tenderRefundedTip",
        sum(CASE WHEN r."paymentMethod" = 'gift-card' THEN coalesce(r."refundedTip", 0) ELSE 0 END) as "giftCardRefundedTip",
        sum(coalesce(r."refundedTax", 0)) as "refundedTax",
        sum(coalesce(r."refundedGratuityAmount", 0)) as "refundedGratuityAmount",
        sum(coalesce(r."refundedserviceChargeAmount", 0)) as "refundedserviceChargeAmount",
        r."ticketId" as "ticketId"

      from ${current_tenant}.refund r
      inner join ${current_tenant}.ticket t on t.id = r."ticketId"
      WHERE t."createdAt" >= $2
          AND t."createdAt" < $3
      group by "ticketId"
    ),
    discount_cte AS (
        SELECT
         t.id as "ticketId",
        SUM(COALESCE((t."paymentInfo" ->> 'discount')::numeric, 0)) AS discount_amount
          FROM ${current_tenant}.ticket t
        WHERE t."restaurantId" = $1
          AND t."createdAt" >= $2
          AND t."createdAt" < $3
          ${paymentFilter}
        group by  t.id
    ),
    tips_cte AS (
      select
		  sum(CASE WHEN p."paymentMethod" = '${PaymentMethod.Card}' THEN coalesce(p.tips, 0) ELSE 0 END) as credit_card_tips,
		  sum(CASE WHEN p."paymentMethod" = '${PaymentMethod.Tender}' THEN coalesce(p.tips, 0) ELSE 0 END) as tender_tips,
      sum(CASE WHEN p."paymentMethod" = '${PaymentMethod.Gift_Card}' THEN coalesce(p.tips, 0) ELSE 0 END) as gift_card_tips,
		  t.id as "ticketId"
		  from ${current_tenant}.ticket t
		  left join ${current_tenant}.payment p on p."ticketId" =  t.id
      WHERE t."restaurantId" = $1
        AND p."paymentState" <> '${PaymentState.Canceled}'
        AND t."createdAt" >= $2
        AND t."createdAt" < $3
      group by t.id
    )

    SELECT
      (SUM(COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0)) + SUM(COALESCE((t."paymentInfo" ->> 'discount')::numeric, 0))) - sum(coalesce(r."refundedAmount", 0)) AS total_sales_with_card_tips,
      SUM(COALESCE((t."paymentInfo" ->> 'totalTax')::numeric, 0)) - SUM(COALESCE(r."refundedTax", 0)) AS total_tax,
      SUM(COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0)) - sum(coalesce(r."refundedAmount", 0)) AS net_sales,
      SUM(CASE WHEN t."employeeUserRestaurantId" IS NOT NULL THEN COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0) ELSE 0 END)
        - SUM(CASE WHEN t."employeeUserRestaurantId" IS NOT NULL THEN COALESCE(r."refundedAmount", 0) ELSE 0 END) AS net_sales_employee,
      SUM(COALESCE(tc.credit_card_tips,0)) - SUM(COALESCE(r."cardRefundedTip", 0)) as credit_card_tips,
	    SUM(COALESCE(tc.tender_tips,0)) - SUM(COALESCE(r."tenderRefundedTip", 0)) as tender_tips,
       SUM(COALESCE(tc.gift_card_tips,0)) - SUM(COALESCE(r."giftCardRefundedTip", 0)) as gift_card_tips,
      SUM(COALESCE((t."paymentInfo" ->> 'totalServiceCharge')::numeric, 0)) - SUM(COALESCE((t."paymentInfo" ->> 'gratuityChargesAmount')::numeric, 0)) - SUM(COALESCE(r."refundedserviceChargeAmount", 0)) AS surcharges,
      SUM(COALESCE((t."paymentInfo" ->> 'gratuityChargesAmount')::numeric, 0)) - SUM(COALESCE(r."refundedGratuityAmount", 0)) AS gratuity,
      SUM(dc.discount_amount) as discount_amount
    from
        ${current_tenant}.ticket t
        left join refund_cte r on r."ticketId" = t.id
        left join discount_cte dc on dc."ticketId" = t.id
        left join tips_cte tc on tc."ticketId" = t.id
        where t."restaurantId" = $1 and t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}') and t."ticketPaymentStatus" NOT IN ${extraExclusions} and t."createdAt" >= $2 and t."createdAt" < $3;
    `;
  return query;
}
/**
 * This function generates a SQL query to calculate the total net sales (POS)
 * grouped by each day for a specific restaurant
 */
export function getTotalSalesWithRestaurantQuery(current_tenant: string) {
  const query = `
     WITH refund_cte AS (
      SELECT
          SUM(COALESCE(amount, 0)) AS amount,
          SUM(COALESCE("refundedAmount", 0)) AS "refundedAmount",
          SUM(CASE WHEN "paymentMethod" = '${PaymentMethod.Card}' THEN COALESCE("refundedTip", 0) ELSE 0 END) AS "cardRefundedTip",
          SUM(CASE WHEN "paymentMethod" = '${PaymentMethod.Tender}' THEN COALESCE("refundedTip", 0) ELSE 0 END) AS "tenderRefundedTip",
          SUM(COALESCE("refundedTax", 0)) AS "refundedTax",
          "ticketId"
      FROM ${current_tenant}.refund
      GROUP BY "ticketId"
    )
    SELECT
        DATE(t."createdAt") AS "sale_date",
        SUM(COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0)) - SUM(COALESCE((t."paymentInfo" ->> 'nonRevenueinReportChargesAmount')::numeric, 0)) - SUM(COALESCE(r."refundedAmount", 0)) AS net_sales
    FROM ${current_tenant}.ticket t
    LEFT JOIN refund_cte r ON r."ticketId" = t.id
    WHERE
        t."restaurantId" = $1
        AND t.status != '${StatusType.Void}'
        AND t."ticketPaymentStatus" != '${TicketPaymentStatus.Unpaid}'
    GROUP BY DATE(t."createdAt")
    ORDER BY sale_date DESC;`;
  return query;
}

export function getRefundedAmountQuery(current_tenant: string) {
  const query = `
    select sum(coalesce(amount, 0)) as total_refund_amount, sum(coalesce("refundedTax", 0)) as refunded_tax, sum(coalesce("refundedTip", 0)) as refunded_tip, sum(coalesce("refundedAmount", 0)) as refunded_amount
      from ${current_tenant}.refund r
               inner join ${current_tenant}.ticket t on r."ticketId" = t.id
      where t."restaurantId" = $1
      and r."createdAt" >= $2
      and r."createdAt" < $3`;
  return query;
}

export function getCashTipsQuery(current_tenant: string) {
  const query = `
    select sum(cdl.amount) as cash_tips
    from ${current_tenant}.cash_drawer_logs cdl
    left join ${current_tenant}.cash_drawer c on cdl."cashDrawerId" = c.id
    where (cdl."logType" = '${LogType.DeclareTips}' or cdl."logSubtype" = '${LogSubtype.TipsCollected}')
    and cdl."createdAt" >= $1
    and cdl."createdAt" < $2
    and c."restaurantId" = $3;`;
  return query;
}

export function getCardTipsQuery(current_tenant: string) {
  const query = `
    SELECT SUM(COALESCE((t."paymentInfo" ->> 'tipAmount')::numeric, 0)) AS card_tips
    FROM ${current_tenant}.ticket t
    WHERE
      t."restaurantId" = $1
      AND t."ticketPaymentStatus" IN ('${TicketPaymentStatus.FullPaid}', '${TicketPaymentStatus.PartialPaid}'  , '${TicketPaymentStatus.PartialRefund}', '${TicketPaymentStatus.FullRefund}')
      AND t."createdAt" >= $2
      AND t."createdAt" < $3
    `;
  return query;
}

export function getGratuityChargeAmount(current_tenant: string) {
  return `
    select coalesce(sum((t."paymentInfo" ->> 'gratuityChargesAmount')::numeric), 0) as gratuity_charge_amount
    from ${current_tenant}.ticket t
    where t.status != '${StatusType.Void}'
    and t."ticketPaymentStatus" != '${TicketPaymentStatus.Unpaid}'
    and t."restaurantId" = $1
    and t."createdAt" >= $2
    and t."createdAt" < $3
  `;
}

export function getTotalTaxPercentageQuery(current_tenant: string) {
  return `Select sum(percentage) as total_tax from ${current_tenant}.tax where "restaurantId" = $1`;
}

export function getNewCustomersQuery(current_tenant: string) {
  return `
        SELECT COUNT(c.id) as count
        from ${current_tenant}.customer c
        left join ${current_tenant}.restaurant_customer_relation rcr on c.id = rcr."customerId"
        where rcr."restaurantId" = $1
        and c."createdAt" >= $2;
  `;
}

export function getEmployeeReviewCountQuery(current_tenant: string) {
  return `
        WITH ratings AS (
          SELECT er.rating, COUNT(*) as ratingCount
          FROM ${current_tenant}.employee_rating er
          INNER JOIN ${current_tenant}."user_restaurants" eur ON eur."id" = er."employeeUserRestaurantId"
          INNER JOIN ${current_tenant}.employee e ON eur."userId" = e.id
          WHERE eur."restaurantId" = $1
          AND
          er."createdAt" >= $2
          AND
          er."createdAt" < $3
          GROUP BY er.rating
        ), all_ratings AS (
          SELECT UNNEST(ARRAY[1, 2, 3, 4, 5]) as rating
        )
        SELECT COALESCE(jsonb_object_agg(ar.rating::text, COALESCE(rs.ratingCount, 0))::text, '{}') as reviewCounts
        FROM all_ratings ar
        LEFT JOIN ratings rs ON ar.rating = rs.rating;
    `;
}

export function getRefundedTipsQuery(current_tenant: string) {
  return `
        SELECT COALESCE(SUM(r."refundedTip"),0) AS refunded_tips
        FROM ${current_tenant}.refund r
                 INNER JOIN ${current_tenant}.ticket t ON r."ticketId" = t.id
        WHERE t."restaurantId" = $1
        AND t."createdAt" >= $2
        AND t."createdAt" < $3;
    `;
}

export function getCashRefundsQuery(current_tenant: string) {
  return `
    SELECT sum(coalesce(amount, 0)) as total_refund_amount, sum(coalesce("refundedTax", 0)) as refunded_tax, sum(coalesce("refundedTip", 0)) as refunded_tip, sum(coalesce("refundedAmount", 0)) as refunded_amount
    FROM ${current_tenant}.refund r
        INNER JOIN ${current_tenant}.ticket t on t.id = r."ticketId"
    WHERE r."paymentMethod" = '${PaymentMethod.Cash}'
      AND t."restaurantId" = $1
      AND t."createdAt" >= $2
      AND t."createdAt" < $3`;
}

export function getCardRefundsQuery(current_tenant: string) {
  return `SELECT sum(coalesce(amount, 0)) as total_refund_amount, sum(coalesce("refundedTax", 0)) as refunded_tax, sum(coalesce("refundedTip", 0)) as refunded_tip, sum(coalesce("refundedAmount", 0)) as refunded_amount
    FROM ${current_tenant}.refund r
            INNER JOIN ${current_tenant}.ticket t on t.id = r."ticketId"
    WHERE r."paymentMethod" = '${PaymentMethod.Card}'
      AND t."restaurantId" = $1
      AND t."createdAt" >= $2
      AND t."createdAt" < $3`;
}

export function getUnpaidBreaksQuery(current_tenant: string) {
  return `
        select sum(bi.total_hours) as unpaid_breaks
        from ${current_tenant}.break_info bi
        inner join ${current_tenant}.break b on bi."breakId" = b.id
        where b."restaurantId" = $1
        and bi.is_paid = false
        and bi.created_at >= $2
        and bi.created_at < $3
        and bi."start_time" is not null
        and bi."end_time" is not null
    `;
}

export function getOverviewQuery(current_tenant: string) {
  return `
        WITH ChecksSummary AS (
            SELECT
                COUNT(p.id) AS total_checks
            FROM ${current_tenant}.payment p left join ${current_tenant}."ticket" t on p."ticketId" = t.id
            WHERE
                t."restaurantId" = $1
                AND (
                    p."paymentStatus" IN ('${PaymentStatus.FullPaid}', '${PaymentStatus.PartialPaid}','${PaymentStatus.PartialRefund}', '${PaymentStatus.FullRefund}')
                OR
                    t.status = '${StatusType.Void}')
                AND t."createdAt" >= $2
                AND t."createdAt" < $3
                AND p."paymentState" <> '${PaymentState.Canceled}'
        ),
        unique_orders_today AS (
            SELECT DISTINCT ON ("orderId")
                "noOfGuests"
            FROM ${current_tenant}.ticket
            WHERE "restaurantId" = $1
                AND "createdAt" >= $2
                AND "createdAt" < $3
            ORDER BY "orderId"
        ),
        GuestsSummary AS (
            SELECT COALESCE(SUM("noOfGuests"), 0) AS total_guests
            FROM unique_orders_today
        ),
        refund_cte AS (
          select sum(coalesce(r.amount, 0)) as amount, sum(coalesce(r."refundedAmount", 0)) as "refundedAmount", sum(coalesce(r."refundedTip", 0)) as "refundedTip", sum(coalesce(r."refundedTax", 0)) as "refundedTax", r."ticketId" as "ticketId" from ${current_tenant}.refund r
          inner join ${current_tenant}.ticket t on t.id = r."ticketId"
          where t."createdAt" >= $2
          AND t."createdAt" < $3
          group by "ticketId"
        ),
        PaymentSummary AS (
            SELECT
            SUM(COALESCE((t."paymentInfo" ->> 'tipAmount')::numeric, 0)) - sum(coalesce(r."refundedTip", 0)) AS card_tips,
            coalesce(sum((t."paymentInfo" ->> 'gratuityChargesAmount')::numeric), 0) as gratuity_charge_amount
            FROM ${current_tenant}.ticket t
            left join refund_cte r on r."ticketId" = t.id
            WHERE
            t."restaurantId" = $1
            AND t."ticketPaymentStatus" IN ('${PaymentStatus.FullPaid}', '${PaymentStatus.PartialPaid}', '${PaymentStatus.PartialRefund}', '${PaymentStatus.FullRefund}')
            AND t.status != '${StatusType.Void}'
            AND t."createdAt" >= $2
            AND t."createdAt" < $3
        ),
        UserRoleSummary AS (
            SELECT COUNT(DISTINCT e.id) AS total_FOH
            FROM ${current_tenant}.employee e
            INNER JOIN ${current_tenant}.user_restaurants ur on ur."userId" = e."id"
            INNER JOIN ${current_tenant}.user_role er ON er."userRestaurantId" = ur.id
            INNER JOIN ${current_tenant}.role r ON er."roleId" = r.id
            WHERE
                ur."restaurantId" = $1
                AND (r.tag @> '["FOH"]')
                AND e.id IN (
                    SELECT ur."userId" as "employeeId"
                    FROM ${current_tenant}.time_log tl
                    INNER JOIN ${current_tenant}.user_restaurants ur on ur."id" = tl."employeeUserRestaurantId"
                    WHERE tl."time_in" >= $2 and tl."time_out" IS NULL
                )
        ),
        BOHSummary AS (
            SELECT COUNT(DISTINCT e.id) AS total_BOH
            FROM ${current_tenant}.employee e
            INNER JOIN ${current_tenant}.user_restaurants ur on ur."userId" = e."id"
            INNER JOIN ${current_tenant}.user_role er ON er."userRestaurantId" = ur.id
            INNER JOIN ${current_tenant}.role r ON er."roleId" = r.id
            WHERE
            ur."restaurantId" = $1
            AND r.tag @> '["BOH"]'
            AND e.id IN (
                SELECT ur."userId" as "employeeId"
                FROM ${current_tenant}.time_log tl
                INNER JOIN ${current_tenant}.user_restaurants ur on ur."id" = tl."employeeUserRestaurantId"
                WHERE tl."time_in" >= $2 and tl."time_out" IS NULL
            )
        ),
        TurnaroundTime AS (
            SELECT AVG(AGE(tt.last_payment_time, tt.earliest_ticket_item_time)) as turn_around_time FROM
            (
                SELECT
                    t.id AS ticket_id,
                    MIN(ti."createdAt") AS earliest_ticket_item_time,
                    MAX(p."createdAt") AS last_payment_time
                FROM
                  ${current_tenant}.ticket t
                LEFT JOIN
                  ${current_tenant}.ticket_item ti ON ti."ticketId" = t.id
                left join
                  ${current_tenant}.payment p on p."ticketId" = t.id
                where
                    p."paymentStatus" = '${PaymentStatus.FullPaid}'
                    AND t."restaurantId" = $1
                    AND P."createdAt" >= $2
                    AND p."createdAt" < $3
                GROUP BY
                    t.id
            ) as tt
        ),
        VoidedAmount AS (
          SELECT
            COALESCE(SUM(ti_amount),0) + COALESCE(SUM(ci_amount),0) AS voided_amount
          FROM (
              SELECT
                  SUM(COALESCE((ti."paymentInfo" ->> 'actualPrice')::numeric, ti.price * ti.quantity)) AS ti_amount,
                  0::numeric AS ci_amount
              FROM ${current_tenant}.ticket_item ti
              INNER JOIN ${current_tenant}.ticket t ON t.id = ti."ticketId"
              WHERE t."restaurantId" = $1
                AND ti.status = 'void'
                AND ti."createdAt" >= $2
                AND ti."createdAt" < $3

              UNION ALL

              SELECT
                  0::numeric,
                  SUM(COALESCE((ci."paymentInfo" ->> 'actualPrice')::numeric, ci.price)) AS ci_amount
              FROM ${current_tenant}.custom_item ci
              INNER JOIN ${current_tenant}.ticket t ON t.id = ci."ticketId"
              WHERE t."restaurantId" = $1
                AND ci.status = 'void'
                AND ci."createdAt" >= $2
                AND ci."createdAt" < $3
          ) v
        ),
        ServerCount AS (
            SELECT COUNT(distinct ur."userId") as server_count
            FROM ${current_tenant}.ticket t
            INNER JOIN ${current_tenant}.user_restaurants ur on ur."id" = t."employeeUserRestaurantId"
            WHERE
                t."restaurantId" = $1
                AND t."ticketPaymentStatus" IN ('${TicketPaymentStatus.FullPaid}', '${TicketPaymentStatus.PartialPaid}', '${TicketPaymentStatus.PartialRefund}', '${TicketPaymentStatus.FullRefund}')
                AND t."createdAt" >= $2
                AND t."createdAt" < $3
        )
        SELECT
            sc.server_count,
            ps.card_tips,
            ps.gratuity_charge_amount,
            gs.total_guests,
            cs.total_checks,
            tt.turn_around_time,
            ers.total_foh,
            bohs.total_boh,
            va.voided_amount
        FROM ChecksSummary cs
        JOIN GuestsSummary gs ON true
        JOIN PaymentSummary ps ON true
        JOIN UserRoleSummary ers ON true
        JOIN BOHSummary bohs ON true
        JOIN TurnaroundTime tt ON true
        JOIN VoidedAmount va ON true
        JOIN ServerCount sc ON true;
    `;
}

export function getBreakSummaryQuery(current_tenant: string) {
  return `
    SELECT
        COALESCE(SUM(COALESCE(tl.meal_breaks,0) + COALESCE(tl.rest_breaks,0)),0) AS total_breaks
    FROM ${current_tenant}.time_log tl
    LEFT JOIN ${current_tenant}.user_restaurants ur
        ON ur.id = tl."employeeUserRestaurantId"
    WHERE ur."restaurantId" = $1
      AND tl."created_at" between $2 and $3;`;
}

// cannot be done throgh ticket directly because of payment per hour
export function getSalesByTimeQuery(current_tenant: string) {
  return `
    with refund_cte AS (
      select sum(coalesce(r.amount, 0)) as amount, sum(coalesce(r."refundedAmount", 0)) as "refundedAmount", sum(coalesce(r."refundedTip", 0)) as "refundedTip", sum(coalesce(r."refundedTax", 0)) as "refundedTax", r."ticketId" as "ticketId" from ${current_tenant}.refund r
      inner join ${current_tenant}.ticket t on t.id = r."ticketId"
      where t."createdAt" >= $2
        and t."createdAt" < $3
      group by "ticketId"
    )
        SELECT
	        SUM(COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0)) - SUM(COALESCE((t."paymentInfo" ->> 'nonRevenueinReportChargesAmount')::numeric, 0)) - sum(coalesce(r."refundedAmount", 0)) AS sales,
      	  DATE_TRUNC('hour',t."createdAt" AT TIME ZONE 'UTC') AT TIME ZONE 'UTC' as time
        FROM
          ${current_tenant}.ticket t
          left join refund_cte r on r."ticketId" = t.id
        WHERE
            t."restaurantId" = $1
            AND t."ticketPaymentStatus" IN ('${TicketPaymentStatus.FullPaid}', '${TicketPaymentStatus.PartialPaid}', '${TicketPaymentStatus.PartialRefund}', '${TicketPaymentStatus.FullRefund}')
            AND
                t."createdAt" >= $2
            AND
                t."createdAt" < $3
        GROUP BY DATE_TRUNC('hour', t."createdAt" AT TIME ZONE 'UTC')
        ORDER BY time ASC;
  `;
}

// cannot be done through ticket directly because of payment type
export function getClockOutSummaryQuery(current_tenant: string) {
  return `
      WITH refund_data as (
        select
          p."ticketId",
          p."paymentMethod",
          sum(coalesce(r.amount, 0)) as amount,
          sum(coalesce(r."refundedAmount", 0)) as "refundedAmount",
          sum(coalesce(r."refundedTax", 0)) as "refundedTax",
          sum(coalesce(r."refundedTip", 0)) as "refundedTip"
        from ${current_tenant}.refund r
        inner join ${current_tenant}.payment p on p."id" = r."paymentId"
        inner join ${current_tenant}.ticket t on t.id = r."ticketId"
        where t."createdAt" >= $2
          and t."createdAt" < $3
        group by p."ticketId", p."paymentMethod"
      ),
      refund_data_by_ticket as (
        select
          "ticketId",
          sum(amount) as amount,
          sum("refundedAmount") as "refundedAmount",
          sum("refundedTip") as "refundedTip"
        from refund_data
        group by "ticketId"
      ),
      sales_summary as (
          select
            coalesce(sum(p."paid") filter (where p."paymentMethod" = '${PaymentMethod.Cash}'), 0) -
              coalesce(sum(r."amount") filter (where r."paymentMethod" = '${PaymentMethod.Cash}'), 0)
            as cash_sales,
            coalesce(sum(p."paid") filter (where p."paymentMethod" = '${PaymentMethod.Card}'), 0) -
              coalesce(sum(r."amount") filter (where r."paymentMethod" = '${PaymentMethod.Card}'), 0)
            as card_sales,
           (COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0) + COALESCE((t."paymentInfo" ->> 'discount')::numeric, 0)) - COALESCE((t."paymentInfo" ->> 'nonRevenueinReportChargesAmount')::numeric, 0) - coalesce(rt."refundedAmount", 0)
            as gross_sales,
            COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0) - coalesce(rt."refundedAmount", 0) as net_sales,
            coalesce((t."paymentInfo" ->> 'tipAmount')::numeric, 0) - coalesce(rt."refundedTip", 0) as card_tips,
            coalesce((t."paymentInfo" ->> 'gratuityChargesAmount')::numeric, 0) as gratuity_charge_amount
            from ${current_tenant}.payment p
            inner join ${current_tenant}.ticket t on p."ticketId" = t.id
            left join refund_data r on r."ticketId" = t.id and r."paymentMethod" = p."paymentMethod"
            left join refund_data_by_ticket rt on rt."ticketId" = t.id
            inner join ${current_tenant}.user_restaurants urs on t."employeeUserRestaurantId" = urs.id and urs."userId" = $4
            and t."restaurantId" = $1
            and t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
            and t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}')
            and p."paymentState" != '${PaymentState.Canceled}'
            and t."createdAt" >= $2
            and t."createdAt" < $3
            group by t.id, rt."refundedAmount", rt."refundedTip"
        ),
        time_log_info as (
            select
                min(tl.time_in) as clock_in_time,
                coalesce(max(tl.time_out), CURRENT_TIMESTAMP) as clock_out_time,
                coalesce(sum(tl.total_hours), 0) as total_hours
            from ${current_tenant}.time_log tl
            inner join ${current_tenant}.user_restaurants urs on urs."id" = tl."employeeUserRestaurantId" and urs."userId" = $4
            where urs."restaurantId" = $1
                and tl.time_in >= $2
                and tl.time_in < $3
        )
        select
          sum(coalesce(sales_summary.cash_sales, 0)) as cash_sales,
          sum(coalesce(sales_summary.card_sales, 0)) as card_sales,
          sum(coalesce(sales_summary.gross_sales, 0)) as gross_sales,
          sum(coalesce(sales_summary.net_sales, 0)) as net_sales,
          sum(coalesce(sales_summary.card_tips, 0)) as card_tips,
          sum(coalesce(sales_summary.gratuity_charge_amount, 0)) as gratuity_charge_amount,
          (select clock_in_time from time_log_info) as clock_in_time,
          (select clock_out_time from time_log_info) as clock_out_time,
          (select total_hours from time_log_info) as total_hours
        from
            sales_summary`;
}

export function getEmployeeBreaksStatus(current_tenant: string) {
  return `
      WITH emp AS (
  SELECT ur.id AS user_restaurant_id
  FROM ${current_tenant}.user_restaurants ur
  WHERE ur."userId" = $1
    AND ur."restaurantId" = $2
  LIMIT 1
    ),
    active_shift AS (
      SELECT tl.id AS time_log_id,
            EXTRACT(EPOCH FROM (NOW() - tl.time_in))/3600 AS shift_hours
      FROM ${current_tenant}.time_log tl
      WHERE tl."employeeUserRestaurantId" = (SELECT user_restaurant_id FROM emp)
        AND tl.time_out IS NULL
      ORDER BY tl.time_in DESC
      LIMIT 1
    ),
    law_totals AS (
      SELECT
        'meal'::text AS break_type,
        sbl.meal_breaks AS required_count,
        MIN(bl."shiftHourRangeStart") FILTER (WHERE bl."breakType" = 'meal') AS min_start
      FROM ${current_tenant}.state_break_law sbl
      LEFT JOIN ${current_tenant}.break_laws bl ON bl.state_break_law_id = sbl.id
      GROUP BY sbl.meal_breaks
      UNION ALL
      SELECT
        'rest'::text AS break_type,
        sbl.rest_breaks AS required_count,
        MIN(bl."shiftHourRangeStart") FILTER (WHERE bl."breakType" = 'rest') AS min_start
      FROM ${current_tenant}.state_break_law sbl
      LEFT JOIN ${current_tenant}.break_laws bl ON bl.state_break_law_id = sbl.id
      GROUP BY sbl.rest_breaks
    ),
    break_infos AS (
      SELECT
        LOWER(bi.break_type::text) AS break_type,
        LOWER(bi.status::text) AS status
      FROM ${current_tenant}.break_info bi
      WHERE bi."timeLogId" = (SELECT time_log_id FROM active_shift)
        AND LOWER(bi.status::text) IN ('taken','waived')
    ),
    break_counts AS (
      SELECT
        bi.break_type,
        COUNT(*) FILTER (WHERE bi.status = 'taken') AS taken_count,
        COUNT(*) FILTER (WHERE bi.status = 'waived') AS waived_count
      FROM break_infos bi
      GROUP BY bi.break_type
    ),
    combined AS (
      SELECT
        lt.break_type,
        COALESCE(lt.required_count, 0) AS required_count,
        COALESCE(bc.taken_count, 0) AS taken_count,
        COALESCE(bc.waived_count, 0) AS waived_count,
        lt.min_start,
        (SELECT shift_hours FROM active_shift) AS shift_hours,
        GREATEST(COALESCE(lt.required_count,0) - COALESCE(bc.waived_count,0), 0) AS effective_due
      FROM law_totals lt
      LEFT JOIN break_counts bc ON bc.break_type = lt.break_type
    )
    SELECT jsonb_object_agg(
            c.break_type,
            CASE
              -- before break window starts and nothing logged → NOT_APPLICABLE
              WHEN c.taken_count = 0 AND c.waived_count = 0
                  AND c.required_count > 0
                  AND c.shift_hours < COALESCE(c.min_start, 9999)
              THEN 'NOT_APPLICABLE'

              -- no breaks taken/waived AND required AND shift passed start → NOT_TAKEN
              WHEN c.taken_count = 0 AND c.waived_count = 0
                  AND c.required_count > 0
                  AND c.shift_hours >= COALESCE(c.min_start,0)
              THEN 'NOT_TAKEN'

              -- no requirement & nothing taken → NOT_APPLICABLE
              WHEN c.required_count = 0 AND c.taken_count = 0 THEN 'NOT_APPLICABLE'

              -- all taken
              WHEN c.taken_count >= c.effective_due AND c.effective_due > 0 THEN 'ALL_TAKEN'

              -- partial taken
              WHEN c.taken_count > 0 AND c.taken_count < c.effective_due THEN 'PARTIALLY_TAKEN'

              -- nothing taken but still due
              WHEN c.taken_count = 0 AND c.effective_due > 0 THEN 'NOT_TAKEN'

              ELSE 'NOT_APPLICABLE'
            END
          ) AS break_statuses
    FROM combined c;
  `;
}

export function getGuestSummaryQuery(current_tenant: string) {
  return `
    WITH unique_orders AS (
      SELECT DISTINCT ON ("orderId")
        "orderId",
        "noOfGuests",
        "ticketPaymentStatus",
        "status"
      FROM
        ${current_tenant}.Ticket
      WHERE
        "restaurantId" = $1
        AND "createdAt" >= $2
        AND "createdAt" < $2 + INTERVAL '1 day'
      ORDER BY
        "orderId"
    )
    SELECT
      SUM("noOfGuests") AS "totalGuests",
      SUM(CASE WHEN "ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.PartialPaid}') or "status" = '${StatusType.Void}' THEN "noOfGuests" ELSE 0 END) AS "completedGuests",
      SUM(CASE WHEN ("ticketPaymentStatus" = '${TicketPaymentStatus.Unpaid}' and "status" != '${StatusType.Void}') or "ticketPaymentStatus" = '${TicketPaymentStatus.PartialPaid}' THEN "noOfGuests" ELSE 0 END) AS "seatedGuests"
    FROM
      unique_orders;
    `;
}

export function getClockInSummaryQuery(current_tenant: string) {
  return `
    WITH current_clock_in AS (
      SELECT
        jsonb_array_elements_text(r.tag) AS tag,
        urs."userId" AS "employeeId"
      FROM
        ${current_tenant}.time_log tl
      INNER JOIN
        ${current_tenant}.user_restaurants urs ON urs."id" = tl."employeeUserRestaurantId" AND urs."restaurantId" = $1
      INNER JOIN
        ${current_tenant}.user_role ur ON ur."userRestaurantId" = tl."employeeUserRestaurantId"
      INNER JOIN
        ${current_tenant}.role r ON ur."roleId" = r.id
      WHERE
        tl.time_out IS NULL
        AND tl.time_in >= $2
        AND tl.time_in < $2 + INTERVAL '1 day'
        AND urs.status = 'active'
    ),
    total_tags AS (
      SELECT
        jsonb_array_elements_text(r.tag) AS tag,
        urs."userId" AS "employeeId"
      FROM
        ${current_tenant}.user_restaurants urs
      INNER JOIN
        ${current_tenant}.user_role ur ON ur."userRestaurantId" = urs."id"
      INNER JOIN
        ${current_tenant}.role r ON ur."roleId" = r.id
      WHERE
        urs."restaurantId" = $1 and urs.status = 'active'
    )
    SELECT
      all_tags.tag,
      COALESCE(COUNT(DISTINCT current_clock_in."employeeId"), 0) AS "clockedIn",
      COALESCE(COUNT(DISTINCT total_tags."employeeId"), 0) AS "totalEmployees"
    FROM
      (SELECT DISTINCT jsonb_array_elements_text(tag) AS tag FROM role) all_tags
    LEFT JOIN
      current_clock_in ON all_tags.tag = current_clock_in.tag
    LEFT JOIN
      total_tags ON all_tags.tag = total_tags.tag
    GROUP BY
      all_tags.tag
    ORDER BY
      all_tags.tag
  `;
}

export function getRefundAmountByUserQuery(current_tenant: string) {
  return `select sum(coalesce(amount, 0)) as total_refund_amount, sum(coalesce("refundedTax", 0)) as refunded_tax, sum(coalesce("refundedTip", 0)) as refunded_tip, sum(coalesce("refundedAmount", 0)) as refunded_amount
    from ${current_tenant}.refund r
             inner join ${current_tenant}.ticket t on r."ticketId" = t.id
             LEFT JOIN ${current_tenant}.user_restaurants ur on ur."id" = t."employeeUserRestaurantId"
    where t."restaurantId" = $1
    and ur."userId" = $4
    and r."createdAt" >= $2
    and r."createdAt" < $3`;
}

export function getReviewCountsByUserQuery(current_tenant: string) {
  return `
    WITH ratings AS (
      SELECT er.rating, COUNT(*) as ratingCount
      FROM ${current_tenant}.employee_rating er
      LEFT JOIN ${current_tenant}.user_restaurants ur on ur."id" = er."employeeUserRestaurantId"
      WHERE ur."userId" = $1
      AND
      er."createdAt" >= $2
      AND
      er."createdAt" < $3
      GROUP BY er.rating
    ), all_ratings AS (
      SELECT UNNEST(ARRAY[1, 2, 3, 4, 5]) as rating
    )
    SELECT COALESCE(jsonb_object_agg(ar.rating::text, COALESCE(rs.ratingCount, 0))::text, '{}') as reviewcounts
    FROM all_ratings ar
    LEFT JOIN ratings rs ON ar.rating = rs.rating;
  `;
}

export function getCustomerCountByUserQuery(current_tenant: string) {
  return `SELECT COUNT(c.id) AS count
    FROM ${current_tenant}.customer c
    LEFT JOIN ${current_tenant}.user_restaurants ur on ur."id" = c."employeeUserRestaurantId"
    WHERE
      ur."userId" = $1
    AND
      c."createdAt" >= $2
    AND
      c."createdAt" < $3;`;
}

export function getTipPercentageByUserQuery(current_tenant: string) {
  return `
  SELECT
    CASE
      WHEN (SUM(COALESCE((t."paymentInfo" ->> 'paidAmount')::numeric, 0)) - COALESCE(SUM(r.amount), 0)) = 0
      THEN 0
  ELSE
      ((SUM(COALESCE((t."paymentInfo" ->> 'tipAmount')::numeric, 0)) -  COALESCE(SUM(r."refundedTip"), 0) + (SELECT COALESCE(SUM(cdl.amount),0)
        FROM ${current_tenant}.cash_drawer_logs cdl
        LEFT JOIN ${current_tenant}.user_restaurants ur on ur."id" = cdl."serverUserRestaurantId"
        WHERE
            (cdl."logType" = '${LogType.DeclareTips}' OR
            cdl."logSubtype" = '${LogSubtype.TipsCollected}')
            AND cdl."createdAt" >= $2
            AND ur."userId" = $1)) /
            (SUM(COALESCE((t."paymentInfo" ->> 'paidAmount')::numeric, 0)) - COALESCE(SUM(r.amount), 0))) * 100
    END
        as tip_percentage
        FROM ${current_tenant}.ticket t
        LEFT JOIN ${current_tenant}.user_restaurants ur on ur."id" = t."employeeUserRestaurantId"
        LEFT JOIN
        (
            SELECT "ticketId", SUM(coalesce("refundedTip", 0)) as "refundedTip", SUM(coalesce(amount, 0)) as amount
            FROM ${current_tenant}.refund r
            GROUP BY "ticketId"
        ) r ON r."ticketId" = t.id
        WHERE t."ticketPaymentStatus" IN ('${PaymentStatus.FullPaid}', '${PaymentStatus.PartialPaid}', '${PaymentStatus.PartialRefund}', '${PaymentStatus.FullRefund}')
        AND ur."userId" = $1
        AND t."createdAt" >= $2
        AND t."createdAt" < $3;
    `;
}

export function getTotalSalesByUserQuery(current_tenant: string) {
  return `
    Select SUM(innersum) as sum
    from
    (
      with refund_cte AS (
        select sum(coalesce(amount, 0)) as amount, sum(coalesce("refundedAmount", 0)) as "refundedAmount", sum(coalesce("refundedTip", 0)) as "refundedTip", sum(coalesce("refundedTax", 0)) as "refundedTax", "ticketId" from ${current_tenant}.refund
        group by "ticketId"
      )
        select
        SUM(COALESCE((t."paymentInfo" ->> 'paidAmount')::numeric, 0)) - sum(coalesce(r.amount, 0)) as innersum
        from ${current_tenant}.ticket t
        LEFT JOIN ${current_tenant}.user_restaurants ur on ur."id" = t."employeeUserRestaurantId"
        LEFT JOIN refund_cte r on r."ticketId" = t.id
        where
            ur."userId" = $1 and
            t."ticketPaymentStatus" IN ('${PaymentStatus.FullPaid}', '${PaymentStatus.PartialPaid}', '${PaymentStatus.PartialRefund}', '${PaymentStatus.FullRefund}')
            AND t."createdAt" >= $2
            AND t."createdAt" < $3

        UNION ALL

        select
            sum(amount) as innersum
        from
          ${current_tenant}.cash_drawer_logs cdl
        LEFT JOIN ${current_tenant}.user_restaurants ur on ur."id" = "serverUserRestaurantId"
        where
            ("logType" = '${LogType.DeclareTips}' or "logSubtype" = '${LogSubtype.TipsCollected}')
            AND ur."userId"= $1
            AND cdl."createdAt" >= $2
            AND cdl."createdAt" < $3
    )a;`;
}

export function getEmployeeCountByUserQuery(current_tenant: string) {
  return `
        SELECT COUNT(*) as count
        FROM ${current_tenant}.employee e
        LEFT JOIN ${current_tenant}.user_restaurants ur on ur."userId" = e."id"
        WHERE ur."restaurantId" = $1`;
}

export function getCustomerCountWithRankByUserQuery(current_tenant: string) {
  return `
    SELECT rank, count, "employeeId" as "employeeId"
    FROM (
      SELECT ur."userId" as "employeeId", COUNT(c.id) AS count, RANK() OVER (ORDER BY COUNT(c.id) DESC) as rank
      FROM ${current_tenant}.customer c
      LEFT JOIN ${current_tenant}.user_restaurants ur on ur."id" = c."employeeUserRestaurantId"
      WHERE
        c."createdAt" >= $3
        AND
        c."createdAt" < $4
        AND
        ur."restaurantId" = $1
      GROUP BY ur."userId"
    ) as subquery
    WHERE "employeeId" = $2;`;
}

export function getTotalTipsWithRankByUserQuery(current_tenant: string) {
  return `
    WITH total_tips AS (
        SELECT
            ur."userId" as "employeeId",
            (COALESCE(SUM(COALESCE((t."paymentInfo" ->> 'tipAmount')::numeric, 0)) - COALESCE(SUM(r."refundedTip"), 0) + (SELECT COALESCE(SUM(cdl.amount),0)
        FROM ${current_tenant}.cash_drawer_logs cdl
        LEFT JOIN ${current_tenant}.user_restaurants urinside on urinside."id" = cdl."serverUserRestaurantId"
        WHERE
            (cdl."logType" = '${LogType.DeclareTips}' OR
            cdl."logSubtype" =
            '${LogSubtype.TipsCollected}')
            AND cdl."createdAt" >= $3
            AND urinside."userId" = ur."userId"),
        0)) as tips
        FROM ${current_tenant}.ticket t
        LEFT JOIN ${current_tenant}.user_restaurants ur on ur."id" = t."employeeUserRestaurantId"
        LEFT JOIN
        (
            SELECT "ticketId", SUM(coalesce("refundedTip", 0)) as "refundedTip", SUM(coalesce(amount, 0)) as amount
            FROM ${current_tenant}.refund
            GROUP BY "ticketId") r ON r."ticketId" = t.id
            WHERE t."ticketPaymentStatus" IN ('${PaymentStatus.FullPaid}', '${PaymentStatus.PartialPaid}', '${PaymentStatus.PartialRefund}', '${PaymentStatus.FullRefund}')
                AND t."restaurantId" = $1
                AND t."createdAt" >= $3
                AND t."createdAt" < $4
            GROUP BY ur."userId"
        ),
        ranked_employees AS (
            SELECT ROW_NUMBER() OVER (ORDER BY total_tips.tips DESC) as rank,
                total_tips.tips,
                total_tips."employeeId"
            FROM total_tips
        )
        SELECT *
        FROM ranked_employees
        WHERE ranked_employees."employeeId" = $2
    `;
}

export function getTotalSalesWithRankByUserQuery(current_tenant: string) {
  return `
    with refund_cte AS (
      select sum(coalesce(amount, 0)) as amount, sum(coalesce("refundedAmount", 0)) as "refundedAmount", sum(coalesce("refundedTip", 0)) as "refundedTip", sum(coalesce("refundedTax", 0)) as "refundedTax", "ticketId" from ${current_tenant}.refund
      where "createdAt" >= $3 and "createdAt" <= $4
      group by "ticketId"
    ),
      total_sales AS (
            SELECT ur."userId" as "employeeId",
            SUM(COALESCE((t."paymentInfo" ->> 'actualPrice')::numeric, 0)) + COALESCE(
                    (
                        SELECT SUM(amount)
                        FROM ${current_tenant}.cash_drawer_logs cdl
                        LEFT JOIN ${current_tenant}.user_restaurants urinside on urinside."id" = "serverUserRestaurantId"
                        WHERE ("logType" = '${LogType.DeclareTips}'
                            OR "logSubtype" = '${LogSubtype.TipsCollected}')
                            AND urinside."userId" = ur."userId"
                            AND cdl."createdAt" >= $3
                            AND cdl."createdAt" < $4
                    ), 0)
                  - COALESCE(
                        (
                            SELECT SUM(coalesce(r."refundedAmount", 0))
                            FROM refund_cte r
                            INNER JOIN ${current_tenant}.ticket t ON r."ticketId" = t.id
                            LEFT JOIN ${current_tenant}.user_restaurants urinside on urinside."id" = t."employeeUserRestaurantId"
                            WHERE urinside."userId" = ur."userId"
                        ),
                    0) AS total_sales
            FROM ${current_tenant}.ticket t
            LEFT JOIN ${current_tenant}.user_restaurants ur on ur."id" = t."employeeUserRestaurantId"
            WHERE t."ticketPaymentStatus" IN ('${PaymentStatus.FullPaid}', '${PaymentStatus.PartialPaid}', '${PaymentStatus.PartialRefund}', '${PaymentStatus.FullRefund}')
                AND t."restaurantId" = $1
                AND t."createdAt" >= $3
                AND t."createdAt" < $4
            GROUP BY ur."userId"),
            ranked_employees AS (
                SELECT ROW_NUMBER() OVER (ORDER BY total_sales.total_sales DESC) as rank,
                  total_sales.total_sales,
                  total_sales."employeeId"
                FROM total_sales
            )
        SELECT *
        FROM ranked_employees
        WHERE ranked_employees."employeeId" = $2;
    `;
}

export function getHourlyLaborReportQuery(current_tenant: string) {
  return `
  with hours as (
  	select ABS(extract(EPOCH FROM ((
  		select min(tl.time_in) from ${current_tenant}.time_log tl where tl.time_in > (select "createdAt" from ${current_tenant}.close_out where "deletedDate" is null and "restaurantId" = $1 and "createdAt"::date = $2::date limit 1)
  		) - (
  		select "createdAt" from ${current_tenant}.close_out where "deletedDate" is null and "restaurantId" = $1 and "createdAt"::date = $3::date limit 1
  		)))/3600) as hours
  ),
    hourly_labor_cost
        as (select sum((coalesce(tl.total_hours, 0) * er.salary) - (coalesce(bi.total_hours, 0) * er.salary)) as salary
            from ${current_tenant}.user_role er
            inner join ${current_tenant}.user_restaurants ur on er."userRestaurantId" = ur."id"
            inner join ${current_tenant}.employee e on ur."userId" = e.id
            inner join ${current_tenant}.time_log tl on ur.id = tl."employeeUserRestaurantId"  and er."roleId" = tl."roleId"
            left join ${current_tenant}.break_info bi on tl.id = bi."timeLogId" and bi.is_paid = false and bi."employeeUserRestaurantId"  = ur.id
            WHERE tl."created_at" >= $2
              and tl."created_at" < $3
              and ur."restaurantId" = $1
              and er."payType" = '${EmployeePayTypeEnum.HOURLY}')
select hlc.salary                                     as labor_cost,
(select hours from hours) as hrs
from hourly_labor_cost hlc;
    `;
}

//TODO: Temporary solution we have to fixed this and apply POS squad solution
export function getHourlyLaborReportQuerysMom(
  current_tenant: string,
  startDate: string,
  endDate: string,
  restaurantId: number
) {
  return `
  with hours as (
  	select ABS(extract(EPOCH FROM ((
  		select min(tl.time_in) from ${current_tenant}.time_log tl where tl.time_in > (select "createdAt" from ${current_tenant}.close_out where "deletedDate" is null and "restaurantId" = ${restaurantId} and "createdAt"::date = '${startDate}'::date limit 1)
  		) - (
  		select "createdAt" from ${current_tenant}.close_out where "deletedDate" is null and "restaurantId" = ${restaurantId} and "createdAt"::date = '${endDate}'::date limit 1
  		)))/3600) as hours
  ),
    hourly_labor_cost
        as (select
          SUM(
            ROUND(
              (
                ROUND(
                  (
                    COALESCE(tl.total_hours,
                      EXTRACT(EPOCH FROM (NOW() - tl.time_in)) / 3600
                    ) - COALESCE(ub.unpaid_break_hours, 0)
                  )::numeric, 2
                )
              ) * er.salary::numeric
            , 2)
          ) AS salary
            from ${current_tenant}.user_role er
            inner join ${current_tenant}.user_restaurants ur on er."userRestaurantId" = ur."id"
            inner join ${current_tenant}.employee e on ur."userId" = e.id
            inner join ${current_tenant}.time_log tl on ur.id = tl."employeeUserRestaurantId"  and er."roleId" = tl."roleId"
            left join lateral (
              select sum(
                case
                  when bi.is_paid = true and bi.break_law_id is not null and bi.total_hours > (bl.duration / 60.0)
                    then round((bi.total_hours - (bl.duration / 60.0))::numeric, 2)
                  when bi.is_paid = false
                    then round(bi.total_hours::numeric, 2)
                  else 0
                end
              ) as unpaid_break_hours
              from ${current_tenant}.break_info bi
              left join ${current_tenant}.break_laws bl on bl.id = bi.break_law_id
              where bi."timeLogId" = tl.id
                and bi.status in ('${BreakInfoStatusEnum.TAKEN}', '${BreakInfoStatusEnum.PARTIALLY_TAKEN}')
            ) ub on true
            WHERE tl."created_at" >= '${startDate}'
              and tl."created_at" < '${endDate}'
              and ur."restaurantId" = ${restaurantId}
              and er."payType" = '${EmployeePayTypeEnum.HOURLY}')
              select hlc.salary  as labor_cost,
              (select hours from hours) as hrs
              from hourly_labor_cost hlc;
    `;
}

export function getGuestReportQuery(current_tenant: string) {
  return `
    with GuestSummary AS (
        select sum(o."noOfGuests") as total_guests from ${current_tenant}."order" o
        left join (select distinct on ("orderId") "orderId", id, "restaurantId", "createdAt" from ${current_tenant}.ticket) t
          on o.id = t."orderId"
        where
          t."restaurantId" = $1
        AND
          t."createdAt" >= $2
        AND
          t."createdAt" < $3
      ),
      refund_cte AS (
        select sum(coalesce(r.amount, 0)) as amount, sum(coalesce(r."refundedAmount", 0)) as "refundedAmount", sum(coalesce(r."refundedTip", 0)) as "refundedTip", sum(coalesce(r."refundedTax", 0)) as "refundedTax", r."ticketId" as "ticketId" from ${current_tenant}.refund r
        inner join ${current_tenant}.ticket t on t.id = r."ticketId"
        where t."createdAt" >= $2
          AND t."createdAt" < $3
        group by "ticketId"
      ),
      SalesSummary as (
        select ((SUM(COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0)) + SUM(COALESCE((t."paymentInfo" ->> 'discount')::numeric, 0))) - SUM(COALESCE((t."paymentInfo" ->> 'nonRevenueinReportChargesAmount')::numeric, 0)) - COALESCE(SUM(r."refundedAmount"), 0)) as total_sales,
        SUM(COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0)) - coalesce(sum(r."refundedAmount"), 0) - SUM(COALESCE((t."paymentInfo" ->> 'nonRevenueinReportChargesAmount')::numeric, 0)) as net_sales
        from ${current_tenant}.ticket t
        left join refund_cte r on t.id = r."ticketId"
        where
          t."restaurantId" = $1
        and
          t."createdAt" >= $2
        and
          t."createdAt" < $3
        and
          t."ticketPaymentStatus" in ('${PaymentStatus.FullPaid}', '${PaymentStatus.PartialPaid}', '${PaymentStatus.PartialRefund}', '${PaymentStatus.FullRefund}')
      ),
      CashTips as (
        select coalesce(SUM(cdl.amount), 0) as cash_tips
        from ${current_tenant}.cash_drawer_logs cdl
        left join ${current_tenant}.cash_drawer c on cdl."cashDrawerId" = c.id
        where
          (cdl."logType" = '${LogType.DeclareTips}' or cdl."logSubtype" = '${LogSubtype.TipsCollected}')
        and
          cdl."createdAt" >= $2
        and
          cdl."createdAt" < $3
        and
          c."restaurantId" = $1
      ),
      OrdersSummary AS (
        SELECT count (DISTINCT t."orderId") as total_orders
        FROM ${current_tenant}.ticket t
        WHERE t."restaurantId" = $1
        AND t."createdAt" >= $2
        AND t."createdAt" < $3
        AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
        AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.FullRefund}', '${TicketPaymentStatus.PartialPaid}')
      )
      select os.total_orders                                                          as orders,
            gs.total_guests                                                           as total_guests,
            (ss.net_sales) / coalesce(NULLIF(gs.total_guests, 0), 1) as avg_spend,
            (ss.net_sales) / coalesce(NULLIF(os.total_orders, 0), 1) as avg_order
        from GuestSummary gs,
            SalesSummary ss,
            CashTips ct,
            OrdersSummary os;

    `;
}

export function getSalesReportQuery(current_tenant: string, partialPaymentStatusDisabled = false) {
  const paymentFilter = partialPaymentStatusDisabled
    ? `
    AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
    AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.PartialPaid}')
  `
    : `
    AND t."ticketPaymentStatus" IN (
      '${TicketPaymentStatus.FullPaid}',
      '${TicketPaymentStatus.PartialPaid}',
      '${TicketPaymentStatus.PartialRefund}',
      '${TicketPaymentStatus.FullRefund}'
    )
  `;

  return `
    with discount_cte AS (
        SELECT SUM(COALESCE((t."paymentInfo" ->> 'discount')::numeric, 0)) AS discount_amount
          FROM ${current_tenant}.ticket t
        WHERE t."restaurantId" = $1
          AND t."createdAt" >= $2
          AND t."createdAt" < $3
          ${paymentFilter}
      ),
      deferred_cte AS (
        SELECT SUM(d.value) AS total_comps
        FROM ${current_tenant}.discount d
        INNER JOIN ${current_tenant}.restaurant r ON d."restaurantId" = r.id
        WHERE d.type = '${DiscountType.Compensation}'
          AND d."restaurantId" = $1
          AND d."createdAt" >= $2
          AND d."createdAt" < $3
      ),
      voided_cte AS (
        SELECT SUM(COALESCE((ti."paymentInfo" ->> 'actualPrice')::numeric, ti.price * ti.quantity)) AS voided_amount
          FROM ${current_tenant}.ticket_item ti
        INNER JOIN ${current_tenant}.ticket t ON t.id = ti."ticketId"
        WHERE t."restaurantId" = $1
          AND ti.status = '${StatusType.Void}'
          AND t."createdAt" >= $2
          AND t."createdAt" < $3
      ),
      custom_voided_cte AS (
        SELECT SUM(COALESCE((ti."paymentInfo" ->> 'actualPrice')::numeric, ti.price)) AS voided_amount
          FROM ${current_tenant}.custom_item ti
        INNER JOIN ${current_tenant}.ticket t ON t.id = ti."ticketId"
        WHERE t."restaurantId" = $1
          AND ti.status = '${StatusType.Void}'
          AND t."createdAt" >= $2
          AND t."createdAt" < $3
      ),
      refunds_cte AS (
        SELECT
          COALESCE(
            SUM(coalesce(r."refundedAmount", 0)) +
            SUM(coalesce(r."refundedTax", 0)) +
            SUM(coalesce(r."refundedTip", 0)) +
            SUM(coalesce(r."refundedGratuityAmount", 0)) +
            SUM(coalesce(r."refundedserviceChargeAmount", 0))
          ,0)
        AS total_refunds
        FROM ${current_tenant}.refund r
        INNER JOIN ${current_tenant}.ticket t ON r."ticketId" = t.id
        WHERE t."restaurantId" = $1
          AND t."createdAt" >= $2
          AND t."createdAt" < $3
      )
      SELECT coalesce(discount_cte.discount_amount, 0) as discount_amount,
      coalesce(deferred_cte.total_comps, 0) as total_comps,
      coalesce(voided_cte.voided_amount, 0) + coalesce(custom_voided_cte.voided_amount, 0) as voided_amount,
      coalesce(refunds_cte.total_refunds, 0) as total_refunds
      FROM
      discount_cte,
      deferred_cte,
      voided_cte,
      custom_voided_cte,
      refunds_cte;
    `;
}

export function getRevenueCenterQuery(current_tenant: string) {
  return `
WITH refund_cte AS (
  SELECT
    r."ticketId",
    SUM(COALESCE(r.amount, 0)) AS amount,
    SUM(COALESCE(r."refundedAmount", 0)) AS "refundedAmount",
    SUM(COALESCE(r."refundedTip", 0)) AS "refundedTip",
    SUM(COALESCE(r."refundedTax", 0)) AS "refundedTax"
  FROM ${current_tenant}.refund r
  INNER JOIN ${current_tenant}.ticket t ON t.id = r."ticketId"
  WHERE t."createdAt" >= $2
    AND t."createdAt" < $3
  GROUP BY r."ticketId"
),
valid_orders AS (
  SELECT DISTINCT t."orderId"
  FROM ${current_tenant}.ticket t
  WHERE t."restaurantId" = $1
    AND t."createdAt" >= $2
    AND t."createdAt" < $3
    AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
    AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.FullRefund}', '${TicketPaymentStatus.PartialPaid}')
),
ticket_data AS (
  SELECT
    t.id AS ticket_id,
    t."orderId",
    COALESCE(f.id, -1) AS floor_id,
    COALESCE(f.name, 'Non Revenue Center') AS floor_name,
    COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0) AS discounted_price,
    COALESCE((t."paymentInfo" ->> 'discount')::numeric, 0) AS discount,
    COALESCE((t."paymentInfo" ->> 'nonRevenueinReportChargesAmount')::numeric, 0) AS non_revenue_charges,
    COALESCE((t."paymentInfo" ->> 'totalTax')::numeric, 0) AS total_tax
  FROM ${current_tenant}.ticket t
  LEFT JOIN ${current_tenant}."table" tb ON t."tableId" = tb.id
  LEFT JOIN ${current_tenant}."floor" f ON tb."floorId" = f.id
  WHERE t."restaurantId" = $1
    AND t."createdAt" >= $2
    AND t."createdAt" < $3
    AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
    AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.PartialPaid}')
)
SELECT
  SUM(td.discounted_price + td.discount - td.non_revenue_charges - COALESCE(r."refundedAmount", 0)) AS total_sales,
  SUM(td.discounted_price - td.non_revenue_charges - COALESCE(r."refundedAmount", 0)) AS net_sales,
  SUM(td.total_tax - COALESCE(r."refundedTax", 0)) AS total_tax,
  COUNT(td.ticket_id) AS total_tickets,
  COUNT(DISTINCT vo."orderId") AS total_orders,
  SUM(td.discount) AS total_discount,
  td.floor_id,
  td.floor_name
FROM ticket_data td
LEFT JOIN refund_cte r ON td.ticket_id = r."ticketId"
LEFT JOIN valid_orders vo ON vo."orderId" = td."orderId"
GROUP BY td.floor_id, td.floor_name
ORDER BY td.floor_name;
`;
}

export function getIncomeAndDeductionQuery(current_tenant: string) {
  return `
  with payment_data as (
    select
      p."ticketId",
      coalesce(sum(p.paid) filter (where p."paymentMethod" = 'cash'), 0) - coalesce(sum(r."refundedAmount") filter (where r."paymentMethod" = 'cash'), 0) - coalesce(sum(r."refundedTax") filter (where r."paymentMethod" = 'cash'), 0) as cash_sale,
      coalesce(sum(p.tips) filter (where p."paymentMethod" = 'card'), 0) - coalesce(sum(r."refundedTip") filter (where r."paymentMethod" = 'card'), 0) as card_tips,
      sum(p."convenienceFee") as stripe_fee
    from
    ${current_tenant}.payment p
    inner join ${current_tenant}.ticket t on t.id = p."ticketId"
    left join ${current_tenant}.refund r on r."paymentId" = p.id
    where t."restaurantId" = $1 and t.status != '${StatusType.Void}' and t."ticketPaymentStatus" != '${TicketPaymentStatus.Unpaid}' and t."createdAt" >= $2 and t."createdAt" < $3
    group by
      p."ticketId"
    )
    select
    sum(house_bank_reimbursment) as house_bank_reimbursement,
    sum(server_tipout_short) as server_tipout_short,
    sum(surcharge_income) as surcharge_income,
    sum(stripe_fee) as card_payment_processing_fee
    from
    (
    select
      t."employeeUserRestaurantId",
      case
        when
    sum(coalesce(pd.cash_sale, 0) - coalesce(pd.card_tips, 0) - coalesce((t."paymentInfo" ->> 'gratuityChargesAmount')::numeric, 0)) < 0
    then abs(sum(coalesce(pd.cash_sale, 0) - coalesce(pd.card_tips, 0) - coalesce((t."paymentInfo" ->> 'gratuityChargesAmount')::numeric, 0)))
        else 0
      end
    as server_tipout_short,
        case
          when
    sum(coalesce(pd.cash_sale, 0) - coalesce(pd.card_tips, 0) - coalesce((t."paymentInfo" ->> 'gratuityChargesAmount')::numeric, 0)) >= 0
    then abs(sum(coalesce(pd.cash_sale, 0) - coalesce(pd.card_tips, 0) - coalesce((t."paymentInfo" ->> 'gratuityChargesAmount')::numeric, 0)))
        else 0
      end
    as house_bank_reimbursment,
          SUM(COALESCE((t."paymentInfo" ->> 'totalServiceCharge')::numeric, 0)) - SUM(COALESCE((t."paymentInfo" ->> 'gratuityChargesAmount')::numeric, 0)) as surcharge_income,
          SUM(COALESCE(pd.stripe_fee,0))/100 as stripe_fee
    from
    ${current_tenant}.ticket t
    left join payment_data pd on
          pd."ticketId" = t.id
    where t."restaurantId" = $1 and t.status != '${StatusType.Void}' and t."ticketPaymentStatus" != '${TicketPaymentStatus.Unpaid}' and t."createdAt" >= $2 and t."createdAt" < $3
    group by
          t."employeeUserRestaurantId"
    ) d
    `;
}

export function getSalesByCategoryQuery(current_tenant: string) {
  return `
    WITH item_prices AS (
      SELECT
        ti.id,
        ti."ticketId",
        i."saleCategory"::text AS sale_category,
      CASE
        WHEN NULLIF(ti."paymentInfo" ->> 'discountedPrice', '') IS NOT NULL
          THEN (ti."paymentInfo" ->> 'discountedPrice')::numeric
        ELSE ti.price * ti.quantity
      END AS item_price,
      CASE 
        WHEN t."ticketPaymentStatus" != '${TicketPaymentStatus.FullRefund}'
          THEN ti.quantity
        ELSE 0
      END AS quantity,
      CASE
        WHEN t."ticketPaymentStatus" != '${TicketPaymentStatus.FullRefund}'
          THEN COALESCE((ti."paymentInfo" ->> 'discount')::numeric, 0)
        ELSE 0
      END AS item_discount
      FROM ${current_tenant}.ticket_item ti
      INNER JOIN ${current_tenant}.item i ON i.id = ti."itemId"
      INNER JOIN ${current_tenant}.ticket t ON t.id = ti."ticketId"
      WHERE ti.status <> 'void'
        AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
        AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.PartialPaid}')
        AND t."restaurantId" = $1
        AND t."createdAt" BETWEEN $2 AND $3
    ),

    custom_item_prices AS (
      SELECT
        ci.id,
        ci."ticketId",
        ci."saleCategory"::text AS sale_category,
      COALESCE((ci."paymentInfo" ->> 'discountedPrice')::numeric, ci.price) AS item_price,
      CASE 
        WHEN t."ticketPaymentStatus" != '${TicketPaymentStatus.FullRefund}'
          THEN ci.quantity
        ELSE 0
      END AS quantity,
      CASE
        WHEN t."ticketPaymentStatus" != '${TicketPaymentStatus.FullRefund}'
          THEN COALESCE((ci."paymentInfo" ->> 'discount')::numeric, 0)
        ELSE 0
      END AS item_discount
      FROM ${current_tenant}.custom_item ci
      JOIN ${current_tenant}.ticket t ON t.id = ci."ticketId"
      WHERE ci.status <> 'void'
        AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
        AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.PartialPaid}')
        AND t."restaurantId" = $1
        AND t."createdAt" BETWEEN $2 AND $3
    ),

    all_item_prices AS (
      SELECT "ticketId", sale_category, item_price, quantity, item_discount FROM item_prices
      UNION ALL
      SELECT "ticketId", sale_category, item_price, quantity, item_discount FROM custom_item_prices
    ),

    refund_cte AS (
      SELECT
        r."ticketId",
        SUM(COALESCE(r."refundedAmount", 0)) AS refunded_amount,
        SUM(COALESCE(r."refundedTax", 0))    AS refunded_tax
      FROM ${current_tenant}.refund r
      JOIN ${current_tenant}.ticket t ON t.id = r."ticketId"
      WHERE t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
        AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.PartialPaid}')
        AND t."restaurantId" = $1
        AND t."createdAt" BETWEEN $2 AND $3
      GROUP BY r."ticketId"
    ),

    ticket_net_cte AS (
      SELECT
        t.id AS "ticketId",
        COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0)
          - COALESCE((t."paymentInfo" ->> 'nonRevenueinReportChargesAmount')::numeric, 0)
          - COALESCE(r.refunded_amount, 0) AS ticket_net_sales,
        CASE
          WHEN t."ticketPaymentStatus" != '${TicketPaymentStatus.FullRefund}'
          THEN COALESCE((t."paymentInfo" ->> 'discount')::numeric, 0)
          ELSE 0
        END AS ticket_discount,
        COALESCE((t."paymentInfo" ->> 'totalTax')::numeric, 0)
          - COALESCE(r.refunded_tax, 0) AS ticket_tax
      FROM ${current_tenant}.ticket t
      LEFT JOIN refund_cte r ON r."ticketId" = t.id
      WHERE t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
        AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.PartialPaid}')
        AND t."restaurantId" = $1
        AND t."createdAt" BETWEEN $2 AND $3
    ),

    item_sum_per_ticket AS (
      SELECT "ticketId", SUM(item_price) AS item_sum
      FROM all_item_prices
      GROUP BY "ticketId"
    ),

    allocatable_items AS (
      SELECT
        ip."ticketId",
        COALESCE(NULLIF(TRIM(ip.sale_category), ''), 'Unknown') AS sale_category,
        CASE
          WHEN ist.item_sum > 0 THEN ip.item_price
          ELSE 0
        END AS item_price,
        COALESCE(ip.item_discount, 0) AS item_discount,
        CASE
          WHEN ist.item_sum > 0
            THEN (ip.item_price / ist.item_sum) * tnc.ticket_net_sales
          ELSE 0
        END AS item_net_sales,
        CASE
          WHEN ist.item_sum > 0
            THEN (ip.item_price / ist.item_sum) * tnc.ticket_tax
          ELSE 0
        END AS item_tax,
        ip.quantity
      FROM all_item_prices ip
      JOIN item_sum_per_ticket ist ON ist."ticketId" = ip."ticketId"
      JOIN ticket_net_cte tnc      ON tnc."ticketId" = ip."ticketId"
    )

    SELECT
      sale_category AS "categoryName",
      SUM(quantity)                                                        AS item_count,
      SUM(COALESCE(item_discount, 0))                                      AS "allDiscounts",
      SUM(COALESCE(item_net_sales, 0))                                     AS net_sales,
      SUM(COALESCE(item_discount, 0)) + SUM(COALESCE(item_net_sales, 0))  AS gross_sales,
      SUM(COALESCE(item_tax, 0))                                           AS total_tax
    FROM allocatable_items
    GROUP BY sale_category
    ORDER BY item_count DESC;
  `;
}

export function getOrderTypeSummaryQuery(current_tenant: string) {
  const orderTypeCase = `CASE
    WHEN LOWER(t."orderSource"::text) IN ('${OrderSource.Doordash}', '${OrderSource.UberEats}', '${OrderSource.GrubHub}') THEN '3rd PartyOrders'
    WHEN LOWER(t."orderType"::text) = '${OrderType.DineIn}' THEN 'Dine In'
    WHEN LOWER(t."orderType"::text) = '${OrderType.Takeaway}' THEN 'Take Away'
    WHEN LOWER(t."orderType"::text) = '${OrderType.Delivery}' THEN 'Delivery'
    WHEN LOWER(t."orderType"::text) = '${OrderType.CateringOrder}' THEN 'Catering Order'
    WHEN LOWER(t."orderType"::text) = '${OrderType.GiftCardTicket}' THEN 'Gift Card Ticket'
    ELSE t."orderType"::text
  END`;
  return `
  WITH refund_cte AS (
    SELECT
      "ticketId",
      SUM(COALESCE(amount, 0)) AS amount,
      SUM(COALESCE("refundedAmount", 0)) AS "refundedAmount",
      SUM(COALESCE("refundedTip", 0)) AS "refundedTip",
      SUM(COALESCE("refundedTax", 0)) AS "refundedTax"
    FROM ${current_tenant}.refund
    GROUP BY "ticketId"
  ),
  discount_cte AS (
    SELECT
      t.id AS "ticketId",
      COALESCE((t."paymentInfo" ->> 'discount')::numeric, 0) AS ticket_discount
    FROM ${current_tenant}.ticket t
    WHERE t."restaurantId" = $1
      AND t."createdAt" >= $2
      AND t."createdAt" < $3
      AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
      AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.PartialPaid}')
  ),
  valid_orders AS (
    SELECT DISTINCT t."orderId"
    FROM ${current_tenant}.ticket t
    WHERE t."restaurantId" = $1
      AND t."createdAt" >= $2
      AND t."createdAt" < $3
      AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
      AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.FullRefund}', '${TicketPaymentStatus.PartialPaid}')
  )

  SELECT
    SUM(COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0))
      - COALESCE(SUM(r."refundedAmount"), 0)
      - SUM(COALESCE((t."paymentInfo" ->> 'nonRevenueinReportChargesAmount')::numeric, 0)) AS net_sales,

    SUM(COALESCE((t."paymentInfo" ->> 'totalTax')::numeric, 0))
      - SUM(COALESCE(r."refundedTax", 0)) AS total_tax,

    SUM(COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0))
      - COALESCE(SUM(r."refundedAmount"), 0)
      - SUM(COALESCE((t."paymentInfo" ->> 'nonRevenueinReportChargesAmount')::numeric, 0)) AS total_sales,

    COUNT(t.id) AS total_tickets,
    COUNT(DISTINCT vo."orderId") AS total_orders,

    SUM(COALESCE(dc.ticket_discount, 0)) AS total_discount,

    ${orderTypeCase} AS order_type

  FROM ${current_tenant}.ticket t
  LEFT JOIN refund_cte r ON r."ticketId" = t.id
  LEFT JOIN discount_cte dc ON dc."ticketId" = t.id
  INNER JOIN ${current_tenant}."order" o ON o.id = t."orderId"
  LEFT JOIN valid_orders vo ON vo."orderId" = t."orderId"

  WHERE t."restaurantId" = $1
    AND t."createdAt" >= $2
    AND t."createdAt" < $3
    AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
    AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.PartialPaid}')

  GROUP BY ${orderTypeCase};
  `;
}

export function getOrderSourceSummaryQuery(current_tenant: string) {
  const orderSourceCase = `CASE
        WHEN LOWER(t."orderSource"::text) = '${OrderSource.Website}' THEN 'Online'
        WHEN LOWER(t."orderSource"::text) = '${OrderSource.Kiosk}' THEN 'Kiosk'
        WHEN LOWER(t."orderSource"::text) = '${OrderSource.POS}' THEN 'POS'
        WHEN LOWER(t."orderSource"::text) = '${OrderSource.QR}' THEN 'QR'
        WHEN LOWER(t."orderSource"::text) = '${OrderSource.MPOS}' THEN 'mPOS'
        WHEN LOWER(t."orderSource"::text) = '${OrderSource.Nugget}' THEN 'Nugget'
        WHEN LOWER(t."orderSource"::text) = '${OrderSource.UberEats}' THEN 'Uber Eats'
        WHEN LOWER(t."orderSource"::text) = '${OrderSource.GrubHub}' THEN 'Grubhub'
        WHEN LOWER(t."orderSource"::text) = '${OrderSource.Doordash}' THEN 'DoorDash'
        ELSE t."orderSource"::text
    END`;

  return `
  WITH refund_cte AS (
    SELECT
      SUM(COALESCE(amount, 0)) AS amount,
      SUM(COALESCE("refundedAmount", 0)) AS "refundedAmount",
      SUM(COALESCE("refundedTip", 0)) AS "refundedTip",
      SUM(COALESCE("refundedTax", 0)) AS "refundedTax",
      "ticketId"
    FROM ${current_tenant}.refund
    GROUP BY "ticketId"
  ),
  valid_orders AS (
    SELECT DISTINCT t."orderId"
    FROM ${current_tenant}.ticket t
    WHERE t."restaurantId" = $1
      AND t."createdAt" >= $2
      AND t."createdAt" < $3
      AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
      AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.FullRefund}', '${TicketPaymentStatus.PartialPaid}')
  )

  SELECT
    SUM(COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0)) AS total_sales,

    SUM(COALESCE((t."paymentInfo" ->> 'totalTax')::numeric, 0)) - COALESCE(SUM(r."refundedTax"), 0) AS total_tax,

    SUM(COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0))
      - COALESCE(SUM(r."refundedAmount"), 0)
      - SUM(COALESCE((t."paymentInfo" ->> 'nonRevenueinReportChargesAmount')::numeric, 0)) AS net_sales,

    COUNT(t.id) AS total_tickets,
    COUNT(DISTINCT vo."orderId") AS total_orders,

    SUM(COALESCE((t."paymentInfo" ->> 'discount')::numeric, 0)) AS total_discount,

    ${orderSourceCase} AS order_source

  FROM ${current_tenant}.ticket t
  LEFT JOIN refund_cte r ON r."ticketId" = t.id
  INNER JOIN ${current_tenant}."order" o ON o.id = t."orderId"
  LEFT JOIN valid_orders vo ON vo."orderId" = t."orderId"

  WHERE t."restaurantId" = $1
    AND t."createdAt" >= $2
    AND t."createdAt" < $3
    AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
    AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.PartialPaid}')

  GROUP BY ${orderSourceCase};
  `;
}

export function getVoidedItemsQuery(current_tenant: string) {
  return `
  SELECT
      SUM(voided_qty) AS voided_qty,
      SUM(voided_amount) AS voided_amount
    FROM (
      SELECT
        SUM(ti.quantity) AS voided_qty,
        SUM(COALESCE((ti."paymentInfo" ->> 'actualPrice')::numeric, ti.price * ti.quantity)) AS voided_amount
      FROM ${current_tenant}.ticket_item ti
      JOIN ${current_tenant}.ticket t ON t.id = ti."ticketId"
      WHERE
        t."restaurantId" = $1
        AND ti.status = '${StatusType.Void}'
        AND t."createdAt" >= $2
        AND t."createdAt" < $3

      UNION ALL

      SELECT
        SUM(ci.quantity) AS voided_qty,
        SUM(COALESCE((ci."paymentInfo" ->> 'actualPrice')::numeric, ci.price)) AS voided_amount
      FROM ${current_tenant}.custom_item ci
      JOIN ${current_tenant}.ticket t ON t.id = ci."ticketId"
      WHERE
        t."restaurantId" = $1
        AND ci.status = '${StatusType.Void}'
        AND t."createdAt" >= $2
        AND t."createdAt" < $3
    ) AS voided_subquery;
    `;
}

export function getVoidedOrdersQuery(current_tenant: string) {
  return `
    SELECT
      COUNT(DISTINCT t.id) AS voided_qty,
      SUM(COALESCE(ti.voided_amount, 0)) + SUM(COALESCE(ci.voided_amount, 0)) AS voided_amount
      FROM ${current_tenant}."ticket" t
      LEFT JOIN (
          SELECT
              ti."ticketId",
              SUM(COALESCE((ti."paymentInfo" ->> 'actualPrice')::numeric, ti.price * ti.quantity)) AS voided_amount
          FROM ${current_tenant}.ticket_item ti
          JOIN ${current_tenant}.ticket t ON t.id = ti."ticketId"
          WHERE ti.status = '${StatusType.Void}'
            AND t."restaurantId" = $1
            AND t."createdAt" >= $2
            AND t."createdAt" < $3
          GROUP BY ti."ticketId"
      ) ti ON ti."ticketId" = t.id
      LEFT JOIN (
          SELECT
              ci."ticketId",
              SUM(COALESCE((ci."paymentInfo" ->> 'actualPrice')::numeric, ci.price)) AS voided_amount
          FROM ${current_tenant}.custom_item ci
          JOIN ${current_tenant}.ticket t ON t.id = ci."ticketId"
          WHERE ci.status = '${StatusType.Void}'
            AND t."restaurantId" = $1
            AND t."createdAt" >= $2
            AND t."createdAt" < $3
          GROUP BY ci."ticketId"
      ) ci ON ci."ticketId" = t.id
          WHERE t.status = '${StatusType.Void}'
          AND t."restaurantId" = $1
          AND t."createdAt" >= $2
          AND t."createdAt" < $3;
`;
}

export function getEmployeeSalesQuery(current_tenant: string) {
  return `
  WITH
refund_ticket_cte AS (
  SELECT
    r."ticketId",
    SUM(COALESCE(r."refundedAmount", 0))                  AS refunded_amount,
    SUM(COALESCE(r."refundedTip", 0))                     AS refunded_tip,
    SUM(COALESCE(r."refundedGratuityAmount"::numeric, 0)) AS refunded_gratuity,
    SUM(COALESCE(r."refundedserviceChargeAmount", 0))     AS refunded_service_charge
  FROM ${current_tenant}.refund r
  INNER JOIN ${current_tenant}.ticket t ON t.id = r."ticketId"
  WHERE t."restaurantId" = $1
    AND t."createdAt" >= $2
    AND t."createdAt" < $3
    AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
    AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}')
  GROUP BY r."ticketId"
),
refund_by_method_cte AS (
  SELECT
    p."ticketId",
    SUM(COALESCE(r."refundedAmount", 0))                  FILTER (WHERE p."paymentMethod" = 'card')                       AS card_refunded_amount,
    SUM(COALESCE(r."refundedTax", 0))                     FILTER (WHERE p."paymentMethod" = 'card')                       AS card_refunded_tax,
    SUM(COALESCE(r."refundedTip", 0))                     FILTER (WHERE p."paymentMethod" = 'card')                       AS card_refunded_tip,
    SUM(COALESCE(r."refundedGratuityAmount"::numeric, 0)) FILTER (WHERE p."paymentMethod" = 'card')                       AS card_refunded_gratuity,
    SUM(COALESCE(r."refundedserviceChargeAmount", 0))     FILTER (WHERE p."paymentMethod" = 'card')                       AS card_refunded_service_charge,
    SUM(COALESCE(r."refundedAmount", 0))                  FILTER (WHERE p."paymentMethod" = 'cash')                       AS cash_refunded_amount,
    SUM(COALESCE(r."refundedTax", 0))                     FILTER (WHERE p."paymentMethod" = 'cash')                       AS cash_refunded_tax,
    SUM(COALESCE(r."refundedTip", 0))                     FILTER (WHERE p."paymentMethod" = 'cash')                       AS cash_refunded_tip,
    SUM(COALESCE(r."refundedGratuityAmount"::numeric, 0)) FILTER (WHERE p."paymentMethod" = 'cash')                       AS cash_refunded_gratuity,
    SUM(COALESCE(r."refundedserviceChargeAmount", 0))     FILTER (WHERE p."paymentMethod" = 'cash')                       AS cash_refunded_service_charge,
    SUM(COALESCE(r."refundedAmount", 0))                  FILTER (WHERE p."paymentMethod" = 'tender')                     AS tender_refunded_amount,
    SUM(COALESCE(r."refundedTax", 0))                     FILTER (WHERE p."paymentMethod" = 'tender')                     AS tender_refunded_tax,
    SUM(COALESCE(r."refundedTip", 0))                     FILTER (WHERE p."paymentMethod" = 'tender')                     AS tender_refunded_tip,
    SUM(COALESCE(r."refundedGratuityAmount"::numeric, 0)) FILTER (WHERE p."paymentMethod" = 'tender')                     AS tender_refunded_gratuity,
    SUM(COALESCE(r."refundedserviceChargeAmount", 0))     FILTER (WHERE p."paymentMethod" = 'tender')                     AS tender_refunded_service_charge,
    SUM(COALESCE(r."refundedTip", 0))                     FILTER (WHERE p."paymentMethod" = '${PaymentMethod.Gift_Card}') AS gift_card_refunded_tip
  FROM ${current_tenant}.refund r
  INNER JOIN ${current_tenant}.payment p ON p.id = r."paymentId"
  GROUP BY p."ticketId"
),
payment_data AS (
  SELECT
    p."ticketId",
    COALESCE(SUM(p.paid) FILTER (WHERE p."paymentMethod" = 'card'),                       0) AS card_paid,
    COALESCE(SUM(p.paid) FILTER (WHERE p."paymentMethod" = 'cash'),                       0) AS cash_paid,
    COALESCE(SUM(p.paid) FILTER (WHERE p."paymentMethod" = 'tender'),                     0) AS tender_paid,
    COALESCE(SUM(p.tips) FILTER (WHERE p."paymentMethod" = 'card'),                       0) AS card_tips,
    COALESCE(SUM(p.tips) FILTER (WHERE p."paymentMethod" = 'tender'),                     0) AS tender_tips,
    COALESCE(SUM(p.tips) FILTER (WHERE p."paymentMethod" = '${PaymentMethod.Gift_Card}'), 0) AS gift_card_tips,
    COALESCE(SUM(p."gratuityAmount"), 0)                                                      AS gratuity_paid,
    COALESCE(SUM(p.tips), 0)                                                                  AS total_tips
  FROM ${current_tenant}.payment p
  INNER JOIN ${current_tenant}.ticket t ON t.id = p."ticketId"
  WHERE t."restaurantId" = $1
    AND t."createdAt" >= $2
    AND t."createdAt" < $3
    AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
    AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}')
    AND p."paymentState" <> '${PaymentState.Canceled}'
  GROUP BY p."ticketId"
),
ticket_cte AS (
  SELECT
    t.id                                                                               AS ticket_id,
    t."orderId"                                                                        AS order_id,
    t."employeeUserRestaurantId",
    COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric,                 0)     AS discounted_price,
    COALESCE((t."paymentInfo" ->> 'nonRevenueinReportChargesAmount')::numeric,  0)    AS non_revenue,
    COALESCE((t."paymentInfo" ->> 'discount')::numeric,                        0)     AS discount_amount
  FROM ${current_tenant}.ticket t
  WHERE t."restaurantId" = $1
    AND t."createdAt" >= $2
    AND t."createdAt" < $3
    AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
    AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}')
),
valid_orders AS (
  SELECT DISTINCT t."orderId"
  FROM ${current_tenant}.ticket t
  WHERE t."restaurantId" = $1
    AND t."createdAt" >= $2
    AND t."createdAt" < $3
    AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
    AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.FullRefund}', '${TicketPaymentStatus.PartialPaid}')
),
cash_tips_cte AS (
  SELECT
    ti."employeeId",
    SUM(COALESCE(ti.amount, 0)) AS cash_tips
  FROM ${current_tenant}.tip ti
  WHERE ti."restaurantId" = $1
    AND ti."createdAt" >= $2
    AND ti."createdAt" < $3
    AND ti."source" = 'cash'
  GROUP BY ti."employeeId"
),
declared_tips_cte AS (
  SELECT
    "employeeId",
    ROUND(SUM(COALESCE(amount, 0))::numeric, 2) AS declared_tip
  FROM ${current_tenant}.tip
  WHERE "paymentId" IS NULL
    AND "createdAt" >= $2
    AND "createdAt" < $3
  GROUP BY "employeeId"
),
paid_tips_cte AS (
  SELECT
    ur."userId"                         AS employee_id,
    COALESCE(SUM(cdl.amount), 0)        AS paid_tips
  FROM ${current_tenant}.cash_drawer_logs cdl
  INNER JOIN ${current_tenant}.user_restaurants ur ON ur.id = cdl."serverUserRestaurantId"
  WHERE cdl."logSubtype" = '${LogSubtype.TipsPaid}'
    AND cdl."createdAt" >= $2
    AND cdl."createdAt" < $3
    AND ur."restaurantId" = $1
  GROUP BY ur."userId"
),
declared_cash_sales_cte AS (
  SELECT
    cdl."serverUserRestaurantId",
    COALESCE(SUM(cdl.amount) FILTER (
      WHERE cdl.reason = '${LogReason.SalesCashDeclaration}'
         OR cdl."logType" = '${LogType.DeclareCash}'
    ), 0) AS declared_cash_sales,
    COALESCE(SUM(cdl.amount) FILTER (WHERE cdl."logType" = '${LogType.CashPayment}' AND cdl."cashDrawerId" IS NOT NULL), 0)
    - COALESCE(SUM(cdl.amount) FILTER (WHERE cdl."logType" = '${LogType.CashRefund}'  AND cdl."cashDrawerId" IS NOT NULL), 0)
      AS sales_already_added_to_drawer
  FROM ${current_tenant}.cash_drawer_logs cdl
  INNER JOIN ${current_tenant}.user_restaurants urs ON urs.id = cdl."serverUserRestaurantId"
  WHERE cdl."createdAt" >= $2
    AND cdl."createdAt" < $3
    AND urs."restaurantId" = $1
  GROUP BY cdl."serverUserRestaurantId"
),
hours_worked_cte AS (
  SELECT
    tl."employeeUserRestaurantId",
    ROUND(SUM(COALESCE(
      tl.total_hours,
      EXTRACT(EPOCH FROM (COALESCE(tl.time_out, NOW() AT TIME ZONE 'utc') - tl.time_in)) / 3600
    ))::numeric, 2) AS hours_worked
  FROM ${current_tenant}.time_log tl
  WHERE tl."created_at" >= $2
    AND tl."created_at" < $3
  GROUP BY tl."employeeUserRestaurantId"
),
roles_cte AS (
  SELECT
    ur.id AS user_restaurant_id,
    JSON_AGG(
      JSON_BUILD_OBJECT('id', COALESCE(r.id, 0), 'role', COALESCE(r."roleName", 'Unassigned'))
      ORDER BY r.id
    ) AS roles
  FROM ${current_tenant}.user_restaurants ur
  LEFT JOIN ${current_tenant}.user_role ur2 ON ur2."userRestaurantId" = ur.id
  LEFT JOIN ${current_tenant}.role r ON r.id = ur2."roleId"
  GROUP BY ur.id
),
voided_data AS (
  SELECT
    v."employeeUserRestaurantId",
    ROUND(SUM(v.actual_price)::numeric, 2) AS voided_amount
  FROM (
    SELECT
      t."employeeUserRestaurantId",
      CASE
        WHEN (ti."paymentInfo"->>'actualPrice') ~ '^-?[0-9]+(\\.[0-9]+)?$'
          THEN (ti."paymentInfo"->>'actualPrice')::numeric
        ELSE ti.price * ti.quantity
      END AS actual_price
    FROM ${current_tenant}.ticket_item ti
    JOIN ${current_tenant}.ticket t ON t.id = ti."ticketId"
    WHERE ti.status = 'void'
      AND t."restaurantId" = $1
      AND t."createdAt" >= $2
      AND t."createdAt" < $3
    UNION ALL
    SELECT
      t."employeeUserRestaurantId",
      CASE
        WHEN (ci."paymentInfo"->>'actualPrice') ~ '^-?[0-9]+(\\.[0-9]+)?$'
          THEN (ci."paymentInfo"->>'actualPrice')::numeric
        ELSE ci.price
      END AS actual_price
    FROM ${current_tenant}.custom_item ci
    JOIN ${current_tenant}.ticket t ON t.id = ci."ticketId"
    WHERE ci.status = 'void'
      AND t."restaurantId" = $1
      AND t."createdAt" >= $2
      AND t."createdAt" < $3
  ) v
  GROUP BY v."employeeUserRestaurantId"
),
total_refunds_cte AS (
  SELECT
    t."employeeUserRestaurantId",
    ROUND(SUM(COALESCE(r.amount, 0))::numeric, 2) AS total_refunds
  FROM ${current_tenant}.refund r
  JOIN ${current_tenant}.ticket t ON t.id = r."ticketId"
  WHERE t."restaurantId" = $1
    AND t."createdAt" >= $2
    AND t."createdAt" < $3
  GROUP BY t."employeeUserRestaurantId"
),
service_charge_data AS (
  SELECT
    t."employeeUserRestaurantId",
    ROUND(SUM(
      COALESCE((t."paymentInfo" ->> 'totalServiceCharge')::numeric,    0)
      - COALESCE((t."paymentInfo" ->> 'gratuityChargesAmount')::numeric, 0)
      - COALESCE(rsc.refunded_sc, 0)
    )::numeric, 2) AS service_charge_amount
  FROM ${current_tenant}.ticket t
  LEFT JOIN (
    SELECT
      "ticketId",
      SUM(COALESCE("refundedserviceChargeAmount", 0)) AS refunded_sc
    FROM ${current_tenant}.refund
    GROUP BY "ticketId"
  ) rsc ON rsc."ticketId" = t.id
  WHERE t."restaurantId" = $1
    AND t."createdAt" >= $2
    AND t."createdAt" < $3
    AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
    AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}')
  GROUP BY t."employeeUserRestaurantId"
)
SELECT
  urs."userId"  AS employee_id,
  e.fullname    AS employee_name,
  ROUND((
    COALESCE(SUM(tc.discounted_price), 0)
    - COALESCE(SUM(rt.refunded_amount), 0)
    - COALESCE(SUM(tc.non_revenue), 0)
  )::numeric, 2) AS net_sales,
  ROUND((
    COALESCE(SUM(pd.card_paid), 0)
    - COALESCE(SUM(rbm.card_refunded_amount), 0)
    - COALESCE(SUM(rbm.card_refunded_tax), 0)
    - COALESCE(SUM(rbm.card_refunded_tip), 0)
    - COALESCE(SUM(rbm.card_refunded_gratuity), 0)
    - COALESCE(SUM(rbm.card_refunded_service_charge), 0)
  )::numeric, 2) AS card_sales,
  ROUND((
    COALESCE(SUM(pd.cash_paid), 0)
    - COALESCE(SUM(rbm.cash_refunded_amount), 0)
    - COALESCE(SUM(rbm.cash_refunded_tax), 0)
    - COALESCE(SUM(rbm.cash_refunded_tip), 0)
    - COALESCE(SUM(rbm.cash_refunded_gratuity), 0)
    - COALESCE(SUM(rbm.cash_refunded_service_charge), 0)
    - COALESCE(MAX(dcs.sales_already_added_to_drawer), 0)
  )::numeric, 2) AS cash_sales,
  ROUND((
    COALESCE(SUM(pd.tender_paid), 0)
    - COALESCE(SUM(rbm.tender_refunded_amount), 0)
    - COALESCE(SUM(rbm.tender_refunded_tax), 0)
    - COALESCE(SUM(rbm.tender_refunded_tip), 0)
    - COALESCE(SUM(rbm.tender_refunded_gratuity), 0)
    - COALESCE(SUM(rbm.tender_refunded_service_charge), 0)
  )::numeric, 2) AS tender_sales,
  ROUND((
    COALESCE(SUM(pd.total_tips), 0)
    - COALESCE(SUM(rt.refunded_tip), 0)
  )::numeric, 2) + COALESCE(MAX(dt.declared_tip), 0) AS tip,
  ROUND((
    COALESCE(SUM(pd.gratuity_paid), 0)
    - COALESCE(SUM(rt.refunded_gratuity), 0)
  )::numeric, 2) AS gratuity,
  ROUND((
    COALESCE(SUM(pd.gratuity_paid), 0)
    - COALESCE(SUM(rt.refunded_gratuity), 0)
  )::numeric, 2) AS gratuity_charge_amount,
  COUNT(DISTINCT CASE WHEN vo."orderId" IS NOT NULL THEN tc.order_id END) AS order_count,
  COALESCE(MAX(hw.hours_worked), 0)               AS total_hours,
  COALESCE(MAX(rc.roles::text)::json, '[]'::json) AS roles,
  ROUND(SUM(tc.discount_amount)::numeric, 2)      AS discount_amount,
  COALESCE(MAX(vd.voided_amount), 0)              AS voided_amount,
  COALESCE(MAX(trc.total_refunds), 0)             AS total_refunds,
  COALESCE(MAX(sc.service_charge_amount), 0)      AS service_charge_amount,
  ROUND((
    COALESCE(SUM(pd.card_tips), 0)
    - COALESCE(SUM(rbm.card_refunded_tip), 0)
  )::numeric, 2) AS card_tips,
  ROUND((
    COALESCE(SUM(pd.tender_tips), 0)
    - COALESCE(SUM(rbm.tender_refunded_tip), 0)
  )::numeric, 2) AS tender_tips,
  ROUND((
    COALESCE(SUM(pd.gift_card_tips), 0)
    - COALESCE(SUM(rbm.gift_card_refunded_tip), 0)
  )::numeric, 2) AS gift_card_tips,
  COALESCE(MAX(ct.cash_tips), 0)            AS cash_tips,
  COALESCE(MAX(dcs.declared_cash_sales), 0) AS declared_cash_sales,
  COALESCE(MAX(pt.paid_tips), 0)            AS paid_tips
FROM ticket_cte tc
LEFT JOIN payment_data pd              ON pd."ticketId"                = tc.ticket_id
LEFT JOIN refund_ticket_cte rt         ON rt."ticketId"                = tc.ticket_id
LEFT JOIN refund_by_method_cte rbm     ON rbm."ticketId"               = tc.ticket_id
LEFT JOIN valid_orders vo              ON vo."orderId"                 = tc.order_id
INNER JOIN ${current_tenant}.user_restaurants urs ON urs.id            = tc."employeeUserRestaurantId"
INNER JOIN ${current_tenant}.employee e           ON e.id              = urs."userId"
LEFT JOIN cash_tips_cte ct             ON ct."employeeId"              = urs."userId"
LEFT JOIN paid_tips_cte pt             ON pt.employee_id               = urs."userId"
LEFT JOIN declared_tips_cte dt         ON dt."employeeId"              = urs."userId"
LEFT JOIN declared_cash_sales_cte dcs  ON dcs."serverUserRestaurantId" = urs.id
LEFT JOIN hours_worked_cte hw          ON hw."employeeUserRestaurantId" = urs.id
LEFT JOIN roles_cte rc                 ON rc.user_restaurant_id        = urs.id
LEFT JOIN voided_data vd               ON vd."employeeUserRestaurantId" = urs.id
LEFT JOIN total_refunds_cte trc        ON trc."employeeUserRestaurantId" = urs.id
LEFT JOIN service_charge_data sc       ON sc."employeeUserRestaurantId" = urs.id
GROUP BY urs."userId", e.id, e.fullname
ORDER BY net_sales DESC;
  `;
}

export function getEmployeeSummaryShiftDetails(current_tenant: string) {
  return `
  select
  tl.time_in as clockin, tl.time_out as clockout, 'Shift ' || row_number() over (order by tl.time_in) as shift
  from ${current_tenant}.time_log tl left join ${current_tenant}.user_restaurants ur on ur.id = tl."employeeUserRestaurantId"
  where ur."restaurantId" = $1 and ur."userId" = $2 and tl.created_at between $3 and $4
  `;
}

export function getEmployeeSettlementDetails(current_tenant: string) {
  return `
  select
  cs."settledAmount" as settlement_amount, cs.type as settlement_type
  from ${current_tenant}.shift_settlement cs
  left join ${current_tenant}.user_restaurants ur on ur.id = cs."employeeUserRestaurantId"
  where ur."restaurantId" = $1 and ur."userId" = $2 and cs."createdAt" between $3 and $4
  `;
}

export function getNetSalesByCategoryQuery(current_tenant: string) {
  return `
    WITH filtered_tickets AS (
      SELECT id
      FROM ${current_tenant}.ticket
      WHERE
        "restaurantId" = $1
        AND "employeeUserRestaurantId" = $2
        AND "createdAt" BETWEEN $3 AND $4
        AND "ticketPaymentStatus" IN ('full-paid', 'partial-refund', 'full-refund', 'refund-initiated', 'unclosed')
        AND "status" != 'void'
    ),

    item_totals AS (
      SELECT
        t.id AS ticket_id,
        x.sale_category,
        SUM(x.item_total) AS item_total,
        SUM(x.item_count) AS item_count
      FROM filtered_tickets t
      JOIN (
        -- TICKET ITEMS
        SELECT
          ti."ticketId" AS ticket_id,
          i."saleCategory" AS sale_category,
          SUM(
            CASE
                WHEN NULLIF(ti."paymentInfo"->>'discountedPrice','') IS NOT NULL
                    THEN (ti."paymentInfo"->>'discountedPrice')::numeric
                ELSE ti.price * ti.quantity
            END
          ) AS item_total,
          SUM(ti.quantity) AS item_count
        FROM ${current_tenant}.ticket_item ti
        JOIN ${current_tenant}.item i
          ON i.id = ti."itemId"
        where ti.status != '${StatusType.Void}'
        GROUP BY ti."ticketId", i."saleCategory"

        UNION ALL

        -- CUSTOM ITEMS
        SELECT
          ci."ticketId" AS ticket_id,
          ci."saleCategory" AS sale_category,
          SUM(
            COALESCE((ci."paymentInfo"->>'discountedPrice')::numeric, ci.price)
          ) AS item_total,
          SUM(ci.quantity) AS item_count
        FROM ${current_tenant}.custom_item ci
        where ci.status != '${StatusType.Void}'
        GROUP BY ci."ticketId", ci."saleCategory"
      ) x ON x.ticket_id = t.id
      GROUP BY t.id, x.sale_category
    ),

    ticket_totals AS (
      SELECT ticket_id, SUM(item_total) AS ticket_total
      FROM item_totals
      GROUP BY ticket_id
    ),

    refund_totals AS (
      SELECT
        p."ticketId" AS ticket_id,
        SUM(COALESCE(r."refundedAmount", 0)) AS total_refund
      FROM ${current_tenant}.payment p
      JOIN ${current_tenant}.refund r
        ON r."paymentId" = p.id
      WHERE r."createdAt" BETWEEN $3 AND $4
        AND p."ticketId" IN (SELECT id FROM filtered_tickets)
      GROUP BY p."ticketId"
    ),

    refund_allocation AS (
      SELECT
        it.sale_category,
        SUM(
          COALESCE(rt.total_refund, 0) *
          (it.item_total / NULLIF(tt.ticket_total, 0))
        ) AS allocated_refund,
        SUM(it.item_total) AS gross_sales,
        SUM(
          CASE
            WHEN COALESCE(rt.total_refund, 0) >= tt.ticket_total THEN 0
            ELSE it.item_count
          END
        ) AS item_count
      FROM item_totals it
      JOIN ticket_totals tt
        ON tt.ticket_id = it.ticket_id
      LEFT JOIN refund_totals rt
        ON rt.ticket_id = it.ticket_id
      GROUP BY it.sale_category
    )

    SELECT
      sale_category,
      (gross_sales - allocated_refund) AS net_sales,
      item_count
    FROM refund_allocation
    WHERE (gross_sales - allocated_refund) > 0
    ORDER BY sale_category;
  `;
}

export function getSalesBySalesCategoryQuery(current_tenant: string) {
  return `
    WITH item_totals AS (
      SELECT
        t.id AS ticket_id,
        x.sale_category,
        SUM(x.item_total) AS item_total,
        SUM(x.quantity) AS item_count
      FROM ${current_tenant}.ticket t
      JOIN (
        -- ------- TICKET ITEMS -------
        SELECT
          ti."ticketId" AS ticket_id,
          i."saleCategory" AS sale_category,
          SUM(
            COALESCE((ti."paymentInfo"->>'discountedPrice')::numeric, ti.price)
            * ti.quantity
          ) AS item_total,
          SUM(ti.quantity) AS quantity
        FROM ${current_tenant}.ticket_item ti
        JOIN ${current_tenant}.item i
          ON i.id = ti."itemId"
        where ti.status != '${StatusType.Void}'
        GROUP BY ti."ticketId", i."saleCategory"

        UNION ALL

        -- ------- CUSTOM ITEMS -------
        SELECT
          ci."ticketId" AS ticket_id,
          ci."saleCategory" AS sale_category,
          SUM(
            COALESCE((ci."paymentInfo"->>'discountedPrice')::numeric, ci.price)
          ) AS item_total,
          SUM(ci.quantity) AS quantity
        FROM ${current_tenant}.custom_item ci
        where ci.status != '${StatusType.Void}'
        GROUP BY ci."ticketId", ci."saleCategory"
      ) x
        ON x.ticket_id = t.id
      WHERE
        t."restaurantId" = $1
        AND t."employeeUserRestaurantId" = $2
        AND t."createdAt" BETWEEN $3 AND $4
      GROUP BY t.id, x.sale_category
    ),

    ticket_totals AS (
      SELECT ticket_id, SUM(item_total) AS ticket_total
      FROM item_totals
      GROUP BY ticket_id
    ),

    -- Detect payment mix
    ticket_payment_methods AS (
      SELECT
        t.id AS ticket_id,
        COUNT(DISTINCT p."paymentMethod") AS payment_method_count
      FROM ${current_tenant}.ticket t
      LEFT JOIN ${current_tenant}.payment p
        ON p."ticketId" = t.id
        AND p."createdAt" BETWEEN $3 AND $4
      WHERE t."restaurantId" = $1
        AND t."employeeUserRestaurantId" = $2
        AND t."createdAt" BETWEEN $3 AND $4
      GROUP BY t.id
    ),

    -- Pre-aggregate refunds per payment to avoid row multiplication
    payment_with_refunds AS (
      SELECT
        p.id AS payment_id,
        p."ticketId",
        p."paymentMethod",
        p."subTotal",
        p."createdAt",
        COALESCE(SUM(r."refundedAmount"), 0) AS total_refunded
      FROM ${current_tenant}.payment p
      LEFT JOIN ${current_tenant}.refund r
        ON r."paymentId" = p.id
        AND r."createdAt" BETWEEN $3 AND $4
      WHERE p."paymentState" != '${PaymentState.Canceled}'
      GROUP BY p.id, p."ticketId", p."paymentMethod", p."subTotal", p."createdAt"
    ),

    payment_totals AS (
      SELECT
        t.id AS ticket_id,
        CASE
          WHEN pwr."paymentMethod" IN ('card','gift-card','tender') THEN 'card'
          WHEN pwr."paymentMethod" = 'cash' THEN 'cash'
          ELSE pwr."paymentMethod"
        END AS payment_group,
        COALESCE(pwr."subTotal", 0)::numeric AS payment_amount,
        pwr.total_refunded AS refunded_amount
      FROM ${current_tenant}.ticket t
      LEFT JOIN payment_with_refunds pwr
        ON pwr."ticketId" = t.id
        AND pwr."createdAt" BETWEEN $3 AND $4
      WHERE t."ticketPaymentStatus" IN ('full-paid', 'full-refund', 'partial-refund','unclosed')
        AND t."createdAt" BETWEEN $3 AND $4
    ),

    payment_item_allocation AS (
      SELECT
        pt.ticket_id,
        pt.payment_group,
        it.sale_category,
        it.item_total,
        it.item_count,
        tt.ticket_total,
        pt.payment_amount - pt.refunded_amount AS value_for_calc,
        it.item_total / NULLIF(tt.ticket_total, 0) AS item_percent
      FROM payment_totals pt
      JOIN ${current_tenant}.ticket t ON t.id = pt.ticket_id
      JOIN ticket_payment_methods tpm ON tpm.ticket_id = pt.ticket_id
      JOIN ticket_totals tt ON tt.ticket_id = pt.ticket_id
      JOIN item_totals it ON it.ticket_id = pt.ticket_id

      -- EXCLUDE MIXED PAYMENT TICKETS
      WHERE tpm.payment_method_count = 1

      -- EXCLUDE SPLIT TICKETS
      AND t."isSplited" = false

      -- EXCLUDE ZERO-TOTAL TICKETS
      AND tt.ticket_total > 0
    ),

    -- Aggregate item counts independently to avoid inflation from payment joins
    category_item_counts AS (
      SELECT
        it.sale_category,
        SUM(it.item_count) AS sold_count
      FROM item_totals it
      JOIN ${current_tenant}.ticket t ON t.id = it.ticket_id
      JOIN ticket_payment_methods tpm ON tpm.ticket_id = it.ticket_id
      JOIN ticket_totals tt ON tt.ticket_id = it.ticket_id
      WHERE tpm.payment_method_count = 1
        AND t."isSplited" = false
        AND tt.ticket_total > 0
        AND t."ticketPaymentStatus" IN ('full-paid', 'full-refund', 'partial-refund')
      GROUP BY it.sale_category
    )

    SELECT
      pia.payment_group AS section,
      pia.sale_category,
      SUM(pia.value_for_calc * pia.item_percent) AS total_sales,
      cic.sold_count AS sold_count
    FROM payment_item_allocation pia
    JOIN category_item_counts cic ON cic.sale_category = pia.sale_category
    GROUP BY pia.payment_group, pia.sale_category, cic.sold_count
    HAVING SUM(pia.value_for_calc * pia.item_percent) > 0
    ORDER BY pia.payment_group, pia.sale_category;
  `;
}

export function getEmployeeSummarySummedData(current_tenant: string) {
  return `WITH
  total_hours AS (SELECT SUM(
    COALESCE(
      tl.total_hours,
      EXTRACT(EPOCH FROM (COALESCE(tl.time_out, (NOW() AT TIME ZONE 'utc')) - tl.time_in)) / 3600
    )
  ) AS "totalHours", ur."userId"
  FROM ${current_tenant}.time_log tl LEFT JOIN ${current_tenant}.user_restaurants ur ON ur.id = tl."employeeUserRestaurantId"
  WHERE ur."userId" = $2 AND ur."restaurantId" = $1 AND tl."created_at" BETWEEN $3 AND $4
  GROUP BY ur."userId" ),

  break_hours AS (SELECT SUM(COALESCE(bi.total_hours,0)) AS "breakHours", ur."userId"
  FROM ${current_tenant}.break_info bi LEFT JOIN ${current_tenant}.user_restaurants ur ON ur.id =bi."employeeUserRestaurantId"
  WHERE ur."userId" = $2 AND ur."restaurantId" = $1 AND bi.status != '${BreakInfoStatusEnum.MEAL_PENALTY}' AND bi."created_at" BETWEEN $3 AND $4
  GROUP BY ur."userId"),
  refund_cte AS (
      select
        sum(coalesce(r."refundedTip", 0)) as "refundedTip",
        SUM(COALESCE(r."refundedGratuityAmount", 0)) AS "refundedGratuity",
        r."ticketId" as "ticketId"
      from ${current_tenant}.refund r
      inner join ${current_tenant}.ticket t on t.id = r."ticketId"
      WHERE t."createdAt" >= $3
          AND t."createdAt" < $4
      group by "ticketId"
    ),

  guests_served AS (
  WITH order_users AS (
    SELECT DISTINCT
      ti."orderId",
      ti."employeeUserRestaurantId" AS emp_ur_id
    FROM ${current_tenant}.ticket ti
    WHERE ti."createdAt" BETWEEN $3 AND $4
      AND ti."restaurantId" = $1
  ),
  order_guests AS (
    SELECT
      ou.emp_ur_id,
      SUM(o."noOfGuests") AS guestsServed
    FROM order_users ou
    JOIN ${current_tenant}."order" o ON o.id = ou."orderId"
    GROUP BY ou.emp_ur_id
  )
  SELECT
    COALESCE(MAX(og.guestsServed), 0) AS "guestsServed",
    ur."userId",
    SUM(
      COALESCE((ti."paymentInfo" ->> 'tipAmount')::numeric, 0) +
      COALESCE((ti."paymentInfo" ->> 'gratuityChargesAmount')::numeric, 0) -
      COALESCE((rc."refundedTip")::numeric, 0) -
      COALESCE((rc."refundedGratuity")::numeric, 0)
    ) AS "totalTipsAndGratuity"
  FROM ${current_tenant}.user_restaurants ur
  JOIN ${current_tenant}.ticket ti
    ON ur.id = ti."employeeUserRestaurantId"
  LEFT JOIN order_guests og
    ON og.emp_ur_id = ur.id
  LEFT JOIN refund_cte rc
    ON rc."ticketId" = ti.id
  WHERE ur."userId" = $2
    AND ur."restaurantId" = $1
    AND ti."createdAt" BETWEEN $3 AND $4
  GROUP BY ur."userId"
  )


  SELECT th."totalHours",bh."breakHours",gs."guestsServed",gs."totalTipsAndGratuity" ,th."userId"
  FROM total_hours th
  LEFT JOIN break_hours bh ON th."userId" = bh."userId"
  LEFT JOIN guests_served gs ON th."userId" = gs."userId"
  `;
}
export function getCashReconciliationData(current_tenant: string, useUpdatedAt = false) {
  const dateColumn = useUpdatedAt ? 'updatedAt' : 'createdAt';

  return `SELECT
  COALESCE(
    SUM(p.paid) FILTER (WHERE p."paymentMethod" = 'cash') -
    SUM(COALESCE(r."refundedAmount",0)) FILTER (WHERE p."paymentMethod" = 'cash') -
    SUM(COALESCE(r."refundedTip",0)) FILTER (WHERE p."paymentMethod" = 'cash') -
    SUM(COALESCE(r."refundedTax",0)) FILTER (WHERE p."paymentMethod" = 'cash') -
    SUM(COALESCE(r."refundedserviceChargeAmount", 0)) FILTER (WHERE p."paymentMethod" = 'cash') -
    SUM(COALESCE(r."refundedGratuityAmount", 0))  FILTER (WHERE p."paymentMethod" = 'cash'), 0
  ) AS "cashCollected",

  COALESCE(
  SUM(p.tips) FILTER (WHERE p."paymentMethod" = 'card') -
  SUM(COALESCE(r."refundedTip",0)) FILTER (WHERE p."paymentMethod" = 'card'), 0
) AS "cardTips",

COALESCE(
  SUM(p.tips) FILTER (WHERE p."paymentMethod" = 'tender') -
  SUM(COALESCE(r."refundedTip",0)) FILTER (WHERE p."paymentMethod" = 'tender'), 0
) AS "tenderTips",

COALESCE(
  SUM(p.tips) FILTER (WHERE p."paymentMethod" = 'gift-card') -
  SUM(COALESCE(r."refundedTip",0)) FILTER (WHERE p."paymentMethod" = 'gift-card'), 0
) AS "giftCardTips",

COALESCE(
  SUM(p.tips) FILTER (WHERE p."paymentMethod" = 'cash') -
  SUM(COALESCE(r."refundedTip",0)) FILTER (WHERE p."paymentMethod" = 'cash'), 0
) AS "cashTips",

  COALESCE(SUM(COALESCE(p."gratuityAmount", 0)) - SUM(COALESCE(r."refundedGratuityAmount", 0)), 0) as gratuity

FROM ${current_tenant}.payment p
INNER JOIN ${current_tenant}.ticket t ON t.id = p."ticketId"
LEFT JOIN ${current_tenant}.refund r ON r."paymentId" = p.id
LEFT JOIN ${current_tenant}.user_restaurants ur ON ur.id = t."employeeUserRestaurantId"
WHERE ur."userId" = $1
  AND ur."restaurantId" = $2
  AND p."paymentState" <> 'canceled'
  AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
  AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}')
  AND t."${dateColumn}" BETWEEN $3 AND $4;

  `;
}

export function getTotalTipsWithOutCashQuery(current_tenant: string) {
  return `
  SELECT
      COALESCE(
        SUM(COALESCE(p.tips, 0))
        - SUM(COALESCE(r."refundedTip", 0)),
      0) AS "totalTips"
    FROM ${current_tenant}.payment p
    INNER JOIN ${current_tenant}.ticket t ON t.id = p."ticketId"
    LEFT JOIN ${current_tenant}.refund r ON r."paymentId" = p.id
    WHERE t."restaurantId" = $1
      AND p."paymentState" <> 'canceled'
      AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
      AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}')
      AND t."createdAt" BETWEEN $2 AND $3;
  `;
}

export function getSalesCashDeclarationData(current_tenant: string) {
  return `SELECT
  COALESCE(
    SUM(cl.amount)
    FILTER (WHERE cl."reason" = 'sales_cash_declaration' OR cl."logType" = '${LogType.DeclareCash}'),
    0
  ) AS declared_cash_sales,

  COALESCE(
    SUM(cl.amount)
    FILTER (WHERE cl."logType" = '${LogType.CashPayment}' AND cl."cashDrawerId" IS NOT NULL),
    0
  )
  -
  COALESCE(
    SUM(cl.amount)
    FILTER (WHERE cl."logType" = '${LogType.CashRefund}' AND cl."cashDrawerId" IS NOT NULL),
    0
  ) AS sales_already_added_to_drawer

FROM ${current_tenant}.cash_drawer_logs cl
INNER JOIN ${current_tenant}.user_restaurants urs ON cl."serverUserRestaurantId" = urs."id"
INNER JOIN ${current_tenant}.employee e ON e."id" = urs."userId"
WHERE urs."userId" = $2 AND urs."restaurantId" = $1 AND cl."createdAt" BETWEEN $3 AND $4`;
}

export function getTransactionsByPaymentMethod(current_tenant: string, type: PaymentMethod) {
  return `
  SELECT
  t."id" AS "id",
  t."ticketNumber" AS "orderNo",
  SUM(COALESCE((p."subTotal")::numeric, 0)) - SUM(COALESCE((r."refundedAmount")::numeric, 0)) AS "netSales",
  SUM(COALESCE((p."tips")::numeric, 0)) - SUM(COALESCE((r."refundedTip")::numeric, 0)) AS tips,
  SUM(COALESCE((p."serviceChargeAmount")::numeric, 0) + COALESCE((p.tax)::numeric, 0)) - SUM(COALESCE((r."refundedTax")::numeric, 0)) - SUM(COALESCE((p."gratuityAmount")::numeric, 0)) - SUM(COALESCE(r."refundedserviceChargeAmount", 0)) AS taxesAndServiceCharges,
  SUM(COALESCE((p."gratuityAmount")::numeric, 0)) - SUM(COALESCE(r."refundedGratuityAmount", 0)) AS gratuity,
  p."paymentStatus" AS "paymentStatus"
  FROM ${current_tenant}.ticket t
  LEFT JOIN ${current_tenant}.payment p ON p."ticketId" = t."id"
  LEFT JOIN ${current_tenant}.refund r ON r."paymentId" = p."id"
  LEFT JOIN ${current_tenant}.user_restaurants ur ON ur.id =t."employeeUserRestaurantId"
  WHERE p."paymentMethod" = '${type}' AND t.status <> '${StatusType.Void}' and p."paymentState" <> '${PaymentState.Canceled}'
  AND ur."userId" = $2 AND ur."restaurantId" = $1 AND t."createdAt" BETWEEN $3 AND $4
  GROUP BY t."id",p."id" ,p."paymentStatus";
 `;
}

export function getTotalSalesForServerCheckout(current_tenant: string) {
  return `WITH
  refund_cte AS (
    SELECT SUM(COALESCE(r."refundedAmount", 0)) AS "refundedAmount",
    SUM(COALESCE(r."refundedTip", 0)) AS "refundedTip",
    SUM(COALESCE(r."refundedTax", 0)) AS "refundedTax",
    SUM(COALESCE(r."refundedserviceChargeAmount", 0)) AS "refundServiceCharges",
    SUM(COALESCE(r."refundedGratuityAmount", 0)) AS "refundedGratuity",
    r."ticketId" as "ticketId"
    FROM ${current_tenant}.refund r
    inner join ${current_tenant}.ticket t on t.id = r."ticketId"
    where t."createdAt" BETWEEN $3 AND $4
    GROUP BY "ticketId"
  ),
  void_cte AS(
    SELECT SUM(COALESCE(("paymentInfo" ->> 'actualPrice')::numeric, price * quantity )) AS "actualPrice", "ticketId"
  FROM ${current_tenant}.ticket_item
    WHERE status = 'void' and "createdAt" BETWEEN $3 AND $4
    GROUP BY "ticketId"
  ),
  custom_void_cte AS(
    SELECT SUM(COALESCE(("paymentInfo" ->> 'actualPrice')::numeric, price)) AS "actualPrice", "ticketId"
  FROM ${current_tenant}.custom_item
    WHERE status = 'void' and "createdAt" BETWEEN $3 AND $4
    GROUP BY "ticketId"
  )
  SELECT SUM(COALESCE((t."paymentInfo" ->> 'discountedPrice')::numeric, 0)
    - COALESCE(r."refundedAmount",0)
    - COALESCE((t."paymentInfo" ->> 'nonRevenueinReportChargesAmount')::numeric, 0)) AS "netSales",
    SUM(COALESCE((t."paymentInfo" ->> 'totalServiceCharge')::numeric, 0)) - SUM(COALESCE((t."paymentInfo" ->> 'gratuityChargesAmount')::numeric, 0)) - SUM(COALESCE((r."refundServiceCharges")::numeric, 0)) as "surcharges",
    SUM(COALESCE((t."paymentInfo" ->> 'discount')::numeric, 0)) AS "discountAndPromos",
    SUM(COALESCE((v."actualPrice")::numeric, 0)) +  SUM(COALESCE((c."actualPrice")::numeric, 0)) AS voids,
    SUM(COALESCE((t."paymentInfo" ->> 'totalTax')::numeric, 0))-SUM(COALESCE(r."refundedTax", 0)) AS taxes,
    SUM(COALESCE(r."refundedAmount", 0)) + SUM(COALESCE(r."refundedTip", 0)) + SUM(COALESCE(r."refundedTax", 0)) + SUM(COALESCE(r."refundServiceCharges", 0)) + SUM(COALESCE(r."refundedGratuity", 0)) AS refunds
    FROM ${current_tenant}.ticket AS t
    LEFT JOIN refund_cte r ON t.id = r."ticketId"
    LEFT JOIN void_cte v ON t.id = v."ticketId"
    LEFT JOIN custom_void_cte c ON t.id = c."ticketId"
    LEFT JOIN ${current_tenant}.user_restaurants ur ON ur.id =t."employeeUserRestaurantId"
    WHERE ur."userId" = $2 AND ur."restaurantId" = $1 AND t."createdAt" BETWEEN $3 AND $4
 `;
}

export function getEmployeeCashTips(current_tenant: string, includeRoleFilter: boolean) {
  return `
    SELECT
      COALESCE(SUM(t.amount), 0) AS "totalTips"
    FROM ${current_tenant}.tip t
    WHERE t."restaurantId" = $1
      AND t."employeeId" = $2
      AND t."createdAt" >= $3
      AND t."createdAt" < $4
      ${includeRoleFilter ? 'AND t."roleId" = $5' : ''}
      AND t."source" = 'cash';
  `;
}

export function getBreakCounts(current_tenant: string) {
  const query = `
     SELECT
          COUNT(bi.id) AS count
        FROM
        ${current_tenant}."break_info" bi
        left join ${current_tenant}.user_restaurants ur on ur.id = bi."employeeUserRestaurantId"
        WHERE
          bi."employeeUserRestaurantId" IS NOT NULL
          AND bi.updated_at BETWEEN $1 AND $2
          AND bi.status = ANY($4)
          AND ur."restaurantId" = $3
        GROUP BY
          ur."restaurantId";
  `;
  return query;
}

export function getLoggedInRolesAgainstEmployee(businessId: number) {
  const query = `
  SELECT tl."roleId" as "roleId"
FROM tenant_${businessId}.time_log AS tl
WHERE tl."employeeUserRestaurantId" = (
    SELECT ur.id
    FROM tenant_${businessId}.user_restaurants AS ur
    INNER JOIN tenant_${businessId}.employee AS e
      ON e.id = ur."userId"
    WHERE e.id = $1
	AND ur."restaurantId" = $2
)
AND tl.created_at BETWEEN $3 AND $4;`;
  return query;
}

export function getGiftCardSalesSummaryQuery(current_tenant: string) {
  return `
    SELECT
      SUM(COALESCE(p.paid, 0) + COALESCE(p.discount, 0)) AS gross_sales,
      SUM(COALESCE(p.discount, 0)) AS discount_amount,
      SUM(COALESCE(p.paid, 0)) AS net_sales
    FROM ${current_tenant}.payment p
    WHERE
      (
        -- Physical Gift Cards
        p."giftCardTicketId" IS NOT NULL
        OR
        -- Digital Gift Cards
        (p."user_gift_card_id" IS NOT NULL AND p."paymentMethod" = 'card')
      )
      AND p."createdAt" >= $1 AND p."createdAt" < $2
  `;
}

export function getGiftCardPaymentsSummaryQuery(current_tenant: string) {
  return `
    SELECT
      p."paymentMethod",
      SUM(COALESCE(p.paid, 0)) as "taxInclusivePayments",
      SUM(COALESCE(p.paid, 0)) as "taxExclusivePayments"
    FROM ${current_tenant}.payment p
    WHERE
      (
        -- Physical
        p."giftCardTicketId" IS NOT NULL
        OR
        -- Digital
        (p."user_gift_card_id" IS NOT NULL AND p."paymentMethod" = 'card')
      )
      AND p."createdAt" >= $1
      AND p."createdAt" < $2
    GROUP BY p."paymentMethod"
  `;
}

export function getGiftCardSalesByServerQuery(current_tenant: string) {
  return `
    SELECT
      COUNT(DISTINCT gct.id) AS sold_count,
      SUM(COALESCE(p.paid, 0) + COALESCE(p.discount, 0)) AS total_amount,
      COUNT(DISTINCT gct.id) FILTER (WHERE COALESCE(p.discount, 0) > 0) AS discount_count,
      SUM(COALESCE(p.discount, 0)) AS discount_amount
    FROM ${current_tenant}.payment p
    INNER JOIN ${current_tenant}.gift_card_ticket gct ON p."giftCardTicketId" = gct.id
    WHERE
      gct."restaurantId" = $1
      AND gct."employeeUserRestaurantId" = $2
      AND p."createdAt" >= $3
      AND p."createdAt" < $4
  `;
}

export function getDiscountsQuery(current_tenant: string) {
  return `
  WITH discounts AS (
  SELECT
    -- Comp Discount per row
    CASE
      WHEN t."orderSource" IN ('${OrderSource.POS}', '${OrderSource.MPOS}')
      THEN COALESCE((t."paymentInfo"->>'discount')::numeric, 0)
           - COALESCE((t."paymentInfo"->>'entireDealDiscount')::numeric, 0)
      ELSE 0
    END AS comp_discount,

    -- Deal Discount per row
    CASE
      WHEN t."orderSource" IN ('${OrderSource.Kiosk}', '${OrderSource.Website}','${OrderSource.QR}')
      THEN COALESCE((t."paymentInfo"->>'discount')::numeric, 0)
      ELSE 0
    END AS deal_discount,

    -- 3PO Discount per row
    CASE
      WHEN t."orderSource" IN ('${OrderSource.Doordash}', '${OrderSource.UberEats}', '${OrderSource.GrubHub}')
      THEN COALESCE((t."paymentInfo"->>'discount')::numeric, 0)
      ELSE 0
    END AS third_party_discount

  FROM ${current_tenant}.ticket t
  WHERE t."restaurantId" = $1
    AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}')
    AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
    AND t."createdAt" BETWEEN $2 AND $3
)

SELECT
  SUM(comp_discount) AS comp_discount,
  SUM(deal_discount) AS deal_discount,
  SUM(third_party_discount) AS third_party_discount,
  SUM(comp_discount + deal_discount + third_party_discount) AS total_discount
FROM discounts;
  `;
}

export function getDailyGiftCardSalesQuery(current_tenant: string) {
  return `
    SELECT
        DATE(p."createdAt") AS "sale_date",
        SUM(COALESCE(p.paid, 0) + COALESCE(p.discount, 0)) AS gross_sales,
        SUM(COALESCE(p.paid, 0)) AS net_sales
    FROM ${current_tenant}.payment p
    WHERE
      (
        -- Physical Gift Cards
        p."giftCardTicketId" IS NOT NULL
        OR
        -- Digital Gift Cards
        (p."user_gift_card_id" IS NOT NULL AND p."paymentMethod" = 'card')
      )
      AND p."createdAt" >= $1
      AND p."createdAt" < $2
    GROUP BY DATE(p."createdAt")
    ORDER BY sale_date DESC
  `;
}

export function getThirdPartyTipsQuery(current_tenant: string) {
  return `
    SELECT
      COALESCE(SUM(COALESCE(p."tips", 0)), 0) AS third_party_tips
    FROM ${current_tenant}.ticket t
    LEFT JOIN ${current_tenant}.payment p ON p."ticketId" = t.id
    WHERE t."restaurantId" = $1
      AND t."thirdPartyOrder" = TRUE
      AND t.status NOT IN ('${StatusType.Void}', '${StatusType.Cancelled}', '${StatusType.Rejected}')
      AND t."ticketPaymentStatus" NOT IN ('${TicketPaymentStatus.Unpaid}', '${TicketPaymentStatus.FullRefund}')
      AND t."createdAt" >= $2
      AND t."createdAt" < $3
  `;
}
